if (!customElements.get('product-info-bulk-order')) {
  customElements.define(
    'product-info-bulk-order',
    class ProductInfoBulkOrder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.optionGroupsElement = this.querySelector('[data-pibulk-option-groups]');
        this.volumeTiersElement = this.querySelector('[data-pibulk-volume-tiers]');
        this.gwpElement = this.querySelector('[data-pibulk-gwp]');
        this.sizesElement = this.querySelector('[data-pibulk-sizes]');
        this.statusElement = this.querySelector('[data-pibulk-status]');
        this.submitButton = this.querySelector('[data-pibulk-submit]');
        this.deliveryElement = this.querySelector('[data-pibulk-delivery]');
        this.standardNoLogoDateElement = this.querySelector('[data-pibulk-standard-no-logo-date]');
        this.standardWithLogoDateElement = this.querySelector('[data-pibulk-standard-with-logo-date]');
        this.expressNoLogoDateElement = this.querySelector('[data-pibulk-express-no-logo-date]');
        this.expressWithLogoDateElement = this.querySelector('[data-pibulk-express-with-logo-date]');
        this.clickCollectNoLogoDateElement = this.querySelector('[data-pibulk-click-collect-no-logo-date]');
        this.clickCollectWithLogoDateElement = this.querySelector('[data-pibulk-click-collect-with-logo-date]');
        this.superfastWithLogoDateElement = this.querySelector('[data-pibulk-superfast-with-logo-date]');
        this.clickCollectDateElement = this.querySelector('[data-pibulk-click-collect-date]');
        this.cutoffTimeElement = this.querySelector('[data-pibulk-cutoff-time]');
        this.cutoffTimeSecondaryElement = this.querySelector('[data-pibulk-cutoff-time-secondary]');
        this.bulkPanelElement = this.querySelector('[data-pibulk-panel="bulk"]');

        this.sizeIndex = Number.parseInt(this.dataset.sizeIndex || '-1', 10);
        this.initialVariantId = String(this.dataset.initialVariantId || '');
        this.handlingTimeDays = this.parseHandlingTimeDays(
          this.dataset.handlingTimeDays,
          this.dataset.handlingTimeRaw
        );
        this.groupPrefix = `pibulk-${this.dataset.sectionId || 'section'}-${this.dataset.blockId || 'block'}`;
        this.productTitle = String(this.dataset.productTitle || document.title || '').trim();
        this.brevoApiKey = String(this.dataset.brevoApiKey || '').trim();
        this.brevoApiEndpoint = String(this.dataset.brevoApiEndpoint || 'https://api.brevo.com/v3/products/alerts').trim();

        this.variants = this.parseJson('[data-pibulk-variants]', []);
        this.variantStockData = this.parseJson('[data-pibulk-variant-stock]', []);
        this.variantStockMap = this.buildVariantStockMap(this.variantStockData);
        this.sizeOrder = this.normalizeSizeOrder(this.parseJson('[data-pibulk-size-order]', []));
        this.optionDefinitions = this.normalizeOptionDefinitions(this.parseJson('[data-pibulk-option-defs]', []));
        this.volumeDiscount = this.normalizeVolumeDiscount(this.parseJson('[data-pibulk-volume-discount]', null));
        this.gwpDiscount = this.normalizeGwpDiscount(this.parseJson('[data-pibulk-gwp-discount]', null));

        if (!Array.isArray(this.variants) || !this.variants.length) {
          this.hidden = true;
          return;
        }

        this.initialVariantId = this.resolveInitialVariantId();

        this.quantities = {};
        this.showOutOfStockVariants = String(this.dataset.showOutOfStockVariants || 'true') !== 'false';
        this.isSubmitting = false;
        this.submittingTotal = 0;
        this.submitDotsTimer = null;
        this.submitDotsFrame = 0;
        this.submitDotsBaseLabel = '';
        this.lastInteractedVariantId = this.initialVariantId;
        this.selectedOptions = this.getInitialSelectedOptions();
        this.normalizeSelectedOptions(-1);
        this.cartQuantitiesByVariantId = {};
        this.swatchHintCleanup = [];
        this.capturedScrollPosition = null;
        this.addedModalId = 'PIBulkAddedModal';
        this.addedModal = null;
        this.handleAddedModalKeydownBound = this.handleAddedModalKeydown.bind(this);
        this.relatedProductJsonCache = {};
        this.relatedProductJsonRequests = {};
        this.relatedColorSyncToken = 0;
        this.backInStockModal = null;
        this.backInStockSubmitting = false;
        this.gwpUiState = {};
        this.handleSubmitBound = this.handleSubmit.bind(this);
        if (this.submitButton) this.submitButton.addEventListener('click', this.handleSubmitBound);
        this.handleCartUpdatedBound = this.handleCartUpdated.bind(this);
        document.addEventListener('cart:updated', this.handleCartUpdatedBound);

        this.handleRelatedRecommendationsLoadedBound = this.handleRelatedRecommendationsLoaded.bind(this);
        this.handleBackInStockKeydownBound = this.handleBackInStockKeydown.bind(this);
        this.initRelatedProductsColorSync();
        this.initDeliveryEstimate();
        this.initBackInStockModal();
        this.render();
        const initialVariant = this.getVariantById(this.initialVariantId);
        if (initialVariant) {
          this.syncMainProductVariant(initialVariant);
        }
        this.syncRelatedProductsToSelectedColor();
        this.loadInitialCartQuantities();
      }

      disconnectedCallback() {
        if (this.submitButton && this.handleSubmitBound) {
          this.submitButton.removeEventListener('click', this.handleSubmitBound);
        }
        if (this.handleCartUpdatedBound) {
          document.removeEventListener('cart:updated', this.handleCartUpdatedBound);
        }
        document.removeEventListener('keydown', this.handleAddedModalKeydownBound);
        this.teardownSwatchOverflowHints();
        if (this.deliveryTimer) {
          window.clearInterval(this.deliveryTimer);
          this.deliveryTimer = null;
        }
        this.teardownRelatedProductsColorSync();
        this.stopSubmitDotsAnimation();
        this.destroyBackInStockModal();
        if (this.mainPriceSyncTimer) {
          window.clearTimeout(this.mainPriceSyncTimer);
          this.mainPriceSyncTimer = null;
        }
        if (this.pendingVariantSyncTimer) {
          window.clearTimeout(this.pendingVariantSyncTimer);
          this.pendingVariantSyncTimer = null;
        }
      }

      parseJson(selector, fallback) {
        try {
          const node = this.querySelector(selector);
          if (!node) return fallback;
          return JSON.parse(node.textContent || 'null') || fallback;
        } catch (error) {
          return fallback;
        }
      }

      parseHandlingTimeDays() {
        for (let index = 0; index < arguments.length; index += 1) {
          const candidate = String(arguments[index] || '').trim();
          if (!candidate) continue;

          const direct = Number.parseFloat(candidate);
          if (Number.isFinite(direct) && direct > 0) {
            return Math.max(1, Math.round(direct));
          }

          const match = candidate.match(/(\d+(?:\.\d+)?)/);
          if (match && match[1]) {
            const extracted = Number.parseFloat(match[1]);
            if (Number.isFinite(extracted) && extracted > 0) {
              return Math.max(1, Math.round(extracted));
            }
          }
        }

        return 0;
      }

      initDeliveryEstimate() {
        if (!this.deliveryElement) return;

        if (!Number.isFinite(this.handlingTimeDays) || this.handlingTimeDays <= 0) {
          this.deliveryElement.hidden = true;
          return;
        }

        this.updateDeliveryEstimate();
        this.deliveryTimer = window.setInterval(() => {
          this.updateDeliveryEstimate();
        }, 60000);
      }

      isWorkingDay(date) {
        const day = date.getDay();
        return day >= 1 && day <= 5;
      }

      moveToNextWorkingDay(date) {
        const next = new Date(date);
        while (!this.isWorkingDay(next)) {
          next.setDate(next.getDate() + 1);
        }
        return next;
      }

      addWorkingDays(date, daysToAdd) {
        const output = new Date(date);
        const targetDays = Math.max(0, Number.parseInt(daysToAdd, 10) || 0);
        let added = 0;

        while (added < targetDays) {
          output.setDate(output.getDate() + 1);
          if (this.isWorkingDay(output)) {
            added += 1;
          }
        }

        return output;
      }

      subtractWorkingDays(date, daysToSubtract) {
        const output = new Date(date);
        const targetDays = Math.max(0, Number.parseInt(daysToSubtract, 10) || 0);
        let removed = 0;

        while (removed < targetDays) {
          output.setDate(output.getDate() - 1);
          if (this.isWorkingDay(output)) {
            removed += 1;
          }
        }

        return output;
      }

      getProcessingStartDate(now) {
        const start = new Date(now);
        start.setSeconds(0, 0);

        if (!this.isWorkingDay(start)) {
          return this.moveToNextWorkingDay(start);
        }

        const cutoff = new Date(start);
        cutoff.setHours(14, 0, 0, 0);
        if (start >= cutoff) {
          const nextDay = new Date(start);
          nextDay.setDate(nextDay.getDate() + 1);
          return this.moveToNextWorkingDay(nextDay);
        }

        return start;
      }

      getNextCutoffDate(now) {
        const cutoff = new Date(now);
        cutoff.setSeconds(0, 0);

        if (this.isWorkingDay(cutoff)) {
          cutoff.setHours(14, 0, 0, 0);
          if (now < cutoff) return cutoff;
        }

        cutoff.setDate(cutoff.getDate() + 1);
        while (!this.isWorkingDay(cutoff)) {
          cutoff.setDate(cutoff.getDate() + 1);
        }
        cutoff.setHours(14, 0, 0, 0);
        return cutoff;
      }

      getOrdinal(value) {
        const day = Number(value) || 0;
        const mod10 = day % 10;
        const mod100 = day % 100;
        if (mod10 === 1 && mod100 !== 11) return 'st';
        if (mod10 === 2 && mod100 !== 12) return 'nd';
        if (mod10 === 3 && mod100 !== 13) return 'rd';
        return 'th';
      }

      formatEstimateDate(date) {
        const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
        const month = date.toLocaleDateString('en-GB', { month: 'long' });
        const day = date.getDate();
        return `${weekday} ${day}${this.getOrdinal(day)} ${month}`;
      }

      formatRemainingTime(msRemaining) {
        const totalMinutes = Math.max(0, Math.floor(msRemaining / 60000));
        const totalHours = Math.floor(totalMinutes / 60);
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        const minutes = totalMinutes % 60;
        if (days > 0) {
          const dayLabel = days === 1 ? 'day' : 'days';
          if (hours > 0) return `${days} ${dayLabel} ${hours} hrs ${minutes} mins`;
          return `${days} ${dayLabel} ${minutes} mins`;
        }
        if (hours <= 0) return `${minutes} mins`;
        return `${hours} hrs ${minutes} mins`;
      }

      updateDeliveryEstimate() {
        if (!this.deliveryElement || this.handlingTimeDays <= 0) return;

        const now = new Date();
        const processingStart = this.getProcessingStartDate(now);
        const standardTransitWorkingDays = 2;
        const expressTransitWorkingDays = 1;
        const logoLeadWorkingDays = 10;

        const standardNoLogoDate = this.addWorkingDays(
          processingStart,
          this.handlingTimeDays + standardTransitWorkingDays
        );
        const standardWithLogoDate = this.addWorkingDays(
          processingStart,
          this.handlingTimeDays + logoLeadWorkingDays + standardTransitWorkingDays
        );
        const expressNoLogoDate = this.addWorkingDays(
          processingStart,
          this.handlingTimeDays + expressTransitWorkingDays
        );
        const expressWithLogoDate = this.addWorkingDays(
          processingStart,
          this.handlingTimeDays + logoLeadWorkingDays + expressTransitWorkingDays
        );
        const clickCollectNoLogoDate = this.subtractWorkingDays(expressNoLogoDate, 1);
        const clickCollectWithLogoDate = this.subtractWorkingDays(expressWithLogoDate, 1);

        if (this.standardNoLogoDateElement) {
          this.standardNoLogoDateElement.textContent = this.formatEstimateDate(standardNoLogoDate);
        }
        if (this.standardWithLogoDateElement) {
          this.standardWithLogoDateElement.textContent = this.formatEstimateDate(standardWithLogoDate);
        }
        if (this.expressNoLogoDateElement) {
          this.expressNoLogoDateElement.textContent = this.formatEstimateDate(expressNoLogoDate);
        }
        if (this.expressWithLogoDateElement) {
          this.expressWithLogoDateElement.textContent = this.formatEstimateDate(expressWithLogoDate);
        }
        if (this.clickCollectNoLogoDateElement) {
          this.clickCollectNoLogoDateElement.textContent = this.formatEstimateDate(clickCollectNoLogoDate);
        }
        if (this.clickCollectWithLogoDateElement) {
          this.clickCollectWithLogoDateElement.textContent = this.formatEstimateDate(clickCollectWithLogoDate);
        }

        // Backward compatibility for older markup still using legacy data attributes.
        // Legacy express/click-collect targets are used in non-logo layouts, so map to non-logo dates.
        if (this.superfastWithLogoDateElement && !this.expressWithLogoDateElement) {
          this.superfastWithLogoDateElement.textContent = this.formatEstimateDate(expressNoLogoDate);
        }
        if (this.clickCollectDateElement && !this.clickCollectWithLogoDateElement) {
          this.clickCollectDateElement.textContent = this.formatEstimateDate(clickCollectNoLogoDate);
        }

        const cutoffDate = this.getNextCutoffDate(now);
        const cutoffText = this.formatRemainingTime(cutoffDate.getTime() - now.getTime());
        if (this.cutoffTimeElement) this.cutoffTimeElement.textContent = cutoffText;
        if (this.cutoffTimeSecondaryElement) this.cutoffTimeSecondaryElement.textContent = cutoffText;
      }

      normalizeOptionText(value) {
        if (value == null) return '';
        if (typeof value === 'object') {
          if (value.name != null) return String(value.name).trim();
          return String(value).trim();
        }
        return String(value).trim();
      }

      getOptionKey(value) {
        return this.normalizeOptionText(value)
          .toLowerCase()
          .replace(/\u00a0/g, ' ')
          .replace(/\s*([/|\\-])\s*/g, '$1')
          .replace(/\s+/g, ' ')
          .trim();
      }

      getVariantImageUrl(variant) {
        if (!variant || typeof variant !== 'object') return '';
        if (variant.featured_image && variant.featured_image.src) return String(variant.featured_image.src);
        if (
          variant.featured_media &&
          variant.featured_media.preview_image &&
          variant.featured_media.preview_image.src
        ) {
          return String(variant.featured_media.preview_image.src);
        }
        if (variant.image && variant.image.src) return String(variant.image.src);
        if (typeof variant.image === 'string' && variant.image) return String(variant.image);
        return '';
      }

      getOptionValueImage(optionIndex, optionLabel) {
        const targetKey = this.getOptionKey(optionLabel);
        if (!targetKey || !Array.isArray(this.variants) || !this.variants.length) return '';

        const matched = this.variants.find((variant) => {
          if (this.getOptionKey(this.getOptionValue(variant, optionIndex)) !== targetKey) return false;
          return Boolean(this.getVariantImageUrl(variant));
        });

        return matched ? this.getVariantImageUrl(matched) : '';
      }

      isVariantSellable(variant) {
        if (!variant || typeof variant !== 'object') return false;
        const stockRecord = this.getVariantStockRecord(variant);
        if (stockRecord && typeof stockRecord.available === 'boolean') return Boolean(stockRecord.available);
        if (typeof variant.available === 'boolean') return Boolean(variant.available);
        return true;
      }

      getVariantsForOptionValue(optionIndex, optionLabel) {
        const targetKey = this.getOptionKey(optionLabel);
        if (!targetKey || !Array.isArray(this.variants) || !this.variants.length) return [];
        return this.variants.filter(
          (variant) => this.getOptionKey(this.getOptionValue(variant, optionIndex)) === targetKey
        );
      }

      isOptionValueFullySoldOut(optionIndex, optionLabel) {
        const matchingVariants = this.getVariantsForOptionValue(optionIndex, optionLabel);
        if (!matchingVariants.length) return false;
        return matchingVariants.every((variant) => !this.isVariantSellable(variant));
      }

      normalizeSizeOrder(values) {
        if (!Array.isArray(values)) return [];

        const output = [];
        const seen = {};
        values.forEach((value) => {
          const label = this.normalizeOptionText(value);
          const key = this.getOptionKey(label);
          if (!label || seen[key]) return;
          seen[key] = true;
          output.push(label);
        });
        return output;
      }

      normalizeOptionDefinitions(definitions) {
        if (!Array.isArray(definitions)) return [];

        const output = [];
        definitions.forEach((definition) => {
          const index = Number.parseInt(definition && definition.index, 10);
          if (Number.isNaN(index) || index < 0 || index === this.sizeIndex) return;

          const type = String(definition.type || 'button').toLowerCase() === 'color' ? 'color' : 'button';
          const name = this.normalizeOptionText(definition.name) || `Option ${index + 1}`;
          const valuesRaw = Array.isArray(definition.values) ? definition.values : [];
          const values = [];
          const seen = {};

          valuesRaw.forEach((item) => {
            const label = this.normalizeOptionText(item && item.label != null ? item.label : item);
            const key = this.getOptionKey(label);
            if (!label || seen[key]) return;
            seen[key] = true;
            const image = item && item.image ? String(item.image) : '';
            const fallbackImage = type === 'color' && !image ? this.getOptionValueImage(index, label) : '';
            values.push({
              label,
              image: image || fallbackImage,
              color: item && item.color ? String(item.color) : '',
              soldOut: type === 'color' ? this.isOptionValueFullySoldOut(index, label) : false,
            });
          });

          if (!values.length) return;
          output.push({ index, name, type, values });
        });

        output.sort((a, b) => a.index - b.index);
        return output;
      }

      buildVariantStockMap(records) {
        const map = {};
        if (!Array.isArray(records)) return map;

        records.forEach((record) => {
          if (!record || record.id == null) return;
          map[String(record.id)] = record;
        });

        return map;
      }

      getVariantId(variant) {
        if (!variant || variant.id == null) return '';
        return String(variant.id);
      }

      getVariantById(id) {
        const target = String(id || '');
        return this.variants.find((variant) => this.getVariantId(variant) === target) || null;
      }

      variantHasMedia(variant) {
        if (!variant || typeof variant !== 'object') return false;
        if (variant.featured_media && variant.featured_media.id != null) return true;
        if (variant.featured_image) {
          const image = variant.featured_image;
          if (image.id != null || image.src || image.url) return true;
        }
        if (variant.image) {
          const image = variant.image;
          if (image.id != null || image.src || image.url) return true;
        }
        if (variant.image_id != null || variant.media_id != null || variant.featured_media_id != null) return true;
        return false;
      }

      getMediaFallbackVariant(variant) {
        const pickByMediaPosition = (candidates) => {
          if (!Array.isArray(candidates) || !candidates.length) return null;
          const withMedia = candidates.filter((candidate) => this.variantHasMedia(candidate));
          if (!withMedia.length) return null;
          withMedia.sort((a, b) => {
            const posA = this.getVariantFeaturedMediaPosition(a) || Number.MAX_SAFE_INTEGER;
            const posB = this.getVariantFeaturedMediaPosition(b) || Number.MAX_SAFE_INTEGER;
            if (posA !== posB) return posA - posB;
            return Number(this.getVariantId(a) || 0) - Number(this.getVariantId(b) || 0);
          });
          return withMedia[0];
        };

        if (this.variantHasMedia(variant)) return variant;

        const filtered = this.getFilteredVariants();
        const match = pickByMediaPosition(filtered);
        if (match) return match;

        const anyMatch = pickByMediaPosition(this.variants);
        return anyMatch || variant;
      }

      getVariantFeaturedMediaId(variant) {
        if (!variant || typeof variant !== 'object') return '';

        const candidates = [
          variant.featured_media && variant.featured_media.id,
          variant.featured_image && variant.featured_image.id,
          variant.image && variant.image.id,
          variant.featured_media_id,
          variant.media_id,
          variant.image_id,
        ];

        for (let index = 0; index < candidates.length; index += 1) {
          const candidate = candidates[index];
          if (candidate == null || candidate === '') continue;
          return String(candidate);
        }

        return '';
      }

      getVariantFeaturedMediaPosition(variant) {
        if (!variant || typeof variant !== 'object') return 0;

        const candidates = [
          variant.featured_media && variant.featured_media.position,
          variant.featured_image && variant.featured_image.position,
          variant.image && variant.image.position,
        ];

        for (let index = 0; index < candidates.length; index += 1) {
          const numeric = Number(candidates[index] || 0);
          if (Number.isFinite(numeric) && numeric > 0) return numeric;
        }

        return 0;
      }

      resolveVariantMediaAnchor(variant) {
        if (!variant || typeof variant !== 'object') return null;

        const featuredMediaId = this.getVariantFeaturedMediaId(variant);
        if (featuredMediaId && Array.isArray(this.mediaData) && this.mediaData.length) {
          const byId = this.mediaData.find((media) => String(media?.id || '') === String(featuredMediaId));
          if (byId) return byId;
        }

        const featuredPosition = this.getVariantFeaturedMediaPosition(variant);
        if (featuredPosition > 0 && Array.isArray(this.mediaData) && this.mediaData.length) {
          const byPosition = this.mediaData.find((media) => Number(media?.position || 0) === featuredPosition);
          if (byPosition) return byPosition;
        }

        const variantImageToken = this.getMediaUrlToken(this.getVariantImageUrl(variant));
        if (variantImageToken && Array.isArray(this.mediaData) && this.mediaData.length) {
          const byToken = this.mediaData.find((media) => media.token && media.token === variantImageToken);
          if (byToken) return byToken;
        }

        return null;
      }

      getAllVariantAnchorPositions() {
        const seen = {};
        const output = [];
        if (!Array.isArray(this.variants) || !this.variants.length) return output;

        this.variants.forEach((variant) => {
          const anchor = this.resolveVariantMediaAnchor(variant);
          const position = Number(anchor?.position || 0);
          if (!Number.isFinite(position) || position <= 0 || seen[position]) return;
          seen[position] = true;
          output.push(position);
        });

        output.sort((a, b) => a - b);
        return output;
      }

      getVariantMediaMarkerIds() {
        if (!Array.isArray(this.variants) || !this.variants.length) return [];
        const seen = {};
        const output = [];

        this.variants.forEach((variant) => {
          const mediaId = this.getVariantFeaturedMediaId(variant);
          if (!mediaId || seen[mediaId]) return;
          seen[mediaId] = true;
          output.push(mediaId);
        });

        return output;
      }

      getOrderedMediaItems(mediaGallery) {
        if (!mediaGallery) return [];
        const mediaItems = this.getMainMediaItems(mediaGallery).filter(
          (item) => !item.classList.contains('swiper-slide-duplicate')
        );

        return mediaItems.sort((a, b) => {
          const indexA = Number(a.dataset.mediaIndex || 0);
          const indexB = Number(b.dataset.mediaIndex || 0);
          return indexA - indexB;
        });
      }

      getMainMediaItems(mediaGallery) {
        if (!mediaGallery) return [];
        const mediaList = mediaGallery.elements && mediaGallery.elements.mediaList ? mediaGallery.elements.mediaList : null;
        if (mediaList) {
          return Array.from(mediaList.querySelectorAll('.product__media-item[data-media-id]'));
        }
        return Array.from(mediaGallery.querySelectorAll('[id^="Slider-Gallery"] .product__media-item[data-media-id]'));
      }

      getThumbnailItems(mediaGallery) {
        if (!mediaGallery) return [];
        const thumbnails = mediaGallery.elements && mediaGallery.elements.thumbnails ? mediaGallery.elements.thumbnails : null;
        if (thumbnails) {
          return Array.from(thumbnails.querySelectorAll('.product__thumbs-item[data-target]'));
        }
        return Array.from(mediaGallery.querySelectorAll('[id^="GalleryThumbnails"] .product__thumbs-item[data-target]'));
      }

      getColorOptionDefinition() {
        return this.optionDefinitions.find((definition) => definition.type === 'color') || null;
      }

      getSelectedColorContext() {
        const colorDefinition = this.getColorOptionDefinition();
        if (!colorDefinition || typeof colorDefinition.index !== 'number') return null;

        const selectedColorLabel = this.selectedOptions[colorDefinition.index] || '';
        const selectedColorKey = this.getOptionKey(selectedColorLabel);
        if (!selectedColorKey) return null;

        const selectedColorValue =
          Array.isArray(colorDefinition.values) &&
          colorDefinition.values.find((value) => this.getOptionKey(value && value.label) === selectedColorKey);
        const selectedSwatchToken = this.getMediaUrlToken(selectedColorValue && selectedColorValue.image);

        return {
          definition: colorDefinition,
          colorKey: selectedColorKey,
          swatchToken: selectedSwatchToken,
        };
      }

      getMediaUrlToken(url) {
        if (!url) return '';
        try {
          const parsed = new URL(String(url), window.location.origin);
          const pathname = String(parsed.pathname || '');
          const filename = pathname.split('/').filter(Boolean).pop() || '';
          return filename.toLowerCase();
        } catch (error) {
          const raw = String(url || '');
          const noQuery = raw.split('?')[0] || raw;
          return (noQuery.split('/').filter(Boolean).pop() || '').toLowerCase();
        }
      }

      normalizeMediaData(records) {
        if (!Array.isArray(records)) return [];

        const output = [];
        records.forEach((record, index) => {
          const rawId = record && record.id != null ? record.id : '';
          const id = String(rawId || '').trim();
          if (!id) return;

          const rawPosition = Number(record && record.position);
          const position = Number.isFinite(rawPosition) && rawPosition > 0 ? rawPosition : index + 1;
          const src = String((record && record.src) || '').trim();
          const alt = String((record && record.alt) || '').trim();

          output.push({
            id,
            position,
            src,
            alt,
            token: this.getMediaUrlToken(src),
          });
        });

        output.sort((a, b) => a.position - b.position);
        return output;
      }

      buildColorMediaMap(entries, mediaData) {
        const map = {};
        if (!Array.isArray(entries) || !entries.length || !Array.isArray(mediaData) || !mediaData.length) return map;

        const mediaById = {};
        mediaData.forEach((media) => {
          if (!media || !media.id) return;
          mediaById[String(media.id)] = media;
        });

        const normalizedEntries = [];
        entries.forEach((entry) => {
          const label = this.normalizeOptionText(entry && entry.label);
          const key = this.getOptionKey(label);
          if (!label || !key) return;

          const variantMediaIds = [];
          const markerSeen = {};
          const rawVariantMediaIds = Array.isArray(entry && entry.variantMediaIds) ? entry.variantMediaIds : [];
          rawVariantMediaIds.forEach((rawId) => {
            const id = String(rawId || '').trim();
            if (!id || markerSeen[id]) return;
            markerSeen[id] = true;
            variantMediaIds.push(id);
          });

          const swatchToken = this.getMediaUrlToken(entry && entry.swatchImage);
          let anchorMedia = null;
          if (swatchToken) {
            anchorMedia = mediaData.find((media) => media.token && media.token === swatchToken) || null;
          }
          if (!anchorMedia && variantMediaIds.length) {
            variantMediaIds.forEach((id) => {
              const candidate = mediaById[id];
              if (!candidate || !candidate.id) return;
              if (!anchorMedia || Number(candidate.position || 0) < Number(anchorMedia.position || 0)) {
                anchorMedia = candidate;
              }
            });
          }

          const anchorId = anchorMedia ? String(anchorMedia.id || '') : '';
          const anchorPositionRaw = anchorMedia ? Number(anchorMedia.position || 0) : 0;
          const anchorPosition = Number.isFinite(anchorPositionRaw) && anchorPositionRaw > 0 ? anchorPositionRaw : 0;

          normalizedEntries.push({
            key,
            label,
            anchorId,
            anchorPosition,
            variantMediaIds,
          });
        });

        const orderedAnchors = normalizedEntries
          .filter((entry) => entry.anchorPosition > 0)
          .sort((a, b) => a.anchorPosition - b.anchorPosition);

        normalizedEntries.forEach((entry) => {
          const orderedIds = [];
          const seen = {};

          if (entry.anchorPosition > 0) {
            const nextAnchor = orderedAnchors.find((anchor) => anchor.anchorPosition > entry.anchorPosition);
            const endPositionExclusive = nextAnchor ? nextAnchor.anchorPosition : Number.POSITIVE_INFINITY;
            mediaData.forEach((media) => {
              if (!media || !media.id) return;
              const position = Number(media.position || 0);
              if (!Number.isFinite(position) || position < entry.anchorPosition || position >= endPositionExclusive) return;
              const mediaId = String(media.id || '');
              if (!mediaId || seen[mediaId]) return;
              seen[mediaId] = true;
              orderedIds.push(mediaId);
            });
          }

          if (!orderedIds.length && Array.isArray(entry.variantMediaIds) && entry.variantMediaIds.length) {
            const fallbackIds = entry.variantMediaIds
              .filter((id) => mediaById[String(id)])
              .sort((a, b) => Number(mediaById[String(a)]?.position || 0) - Number(mediaById[String(b)]?.position || 0));
            fallbackIds.forEach((id) => {
              const mediaId = String(id || '');
              if (!mediaId || seen[mediaId]) return;
              seen[mediaId] = true;
              orderedIds.push(mediaId);
            });
          }

          if (orderedIds.length) {
            map[entry.key] = {
              label: entry.label,
              markerIds: entry.anchorId ? [entry.anchorId] : [],
              mediaIds: orderedIds,
            };
          }
        });

        return map;
      }

      getExplicitColorMediaSet(variant, orderedItems) {
        const colorDefinition = this.getColorOptionDefinition();
        if (!colorDefinition || typeof colorDefinition.index !== 'number') return null;

        const selectedColorLabel =
          this.selectedOptions[colorDefinition.index] || this.getOptionValue(variant, colorDefinition.index) || '';
        const selectedColorKey = this.getOptionKey(selectedColorLabel);
        if (!selectedColorKey) return null;

        const mapping = this.colorMediaMap && this.colorMediaMap[selectedColorKey];
        if (!mapping || !Array.isArray(mapping.mediaIds) || !mapping.mediaIds.length) return null;

        const orderedMediaIds = Array.isArray(orderedItems)
          ? orderedItems.map((item) => String(item?.dataset?.mediaId || '')).filter(Boolean)
          : [];
        if (!orderedMediaIds.length) return null;

        const orderedMediaIdSet = new Set(orderedMediaIds);
        const filteredIds = mapping.mediaIds.filter((id) => orderedMediaIdSet.has(String(id)));
        if (!filteredIds.length) return null;

        return new Set(filteredIds.map((id) => String(id)));
      }

      getMediaItemSourceUrl(item) {
        if (!item) return '';
        const dataSrc = String(item.dataset.src || '').trim();
        if (dataSrc) return dataSrc;
        const image = item.querySelector('img');
        return image ? String(image.currentSrc || image.src || '').trim() : '';
      }

      getColorMarkerPositions(mediaGallery, orderedItems) {
        const colorDefinition = this.getColorOptionDefinition();
        if (!colorDefinition || !Array.isArray(colorDefinition.values) || !colorDefinition.values.length) return [];

        const markers = [];
        const seenIndex = {};

        colorDefinition.values.forEach((value) => {
          const label = this.normalizeOptionText(value && value.label);
          const imageToken = this.getMediaUrlToken(value && value.image);
          if (!label || !imageToken) return;

          const index = orderedItems.findIndex((item) => {
            const itemUrl = this.getMediaItemSourceUrl(item);
            const itemToken = this.getMediaUrlToken(itemUrl);
            return itemToken && itemToken === imageToken;
          });
          if (index < 0 || seenIndex[index]) return;
          seenIndex[index] = true;

          markers.push({
            label,
            key: this.getOptionKey(label),
            index,
          });
        });

        markers.sort((a, b) => a.index - b.index);
        return markers;
      }

      getColorPositionMarkersFromVariants(colorDefinition, orderedItems) {
        if (!colorDefinition || typeof colorDefinition.index !== 'number' || !Array.isArray(orderedItems)) return [];

        const mediaIndexById = {};
        orderedItems.forEach((item, index) => {
          const mediaId = String(item && item.dataset && item.dataset.mediaId ? item.dataset.mediaId : '');
          if (!mediaId) return;
          mediaIndexById[mediaId] = index;
        });

        const indexByColorKey = {};
        this.variants.forEach((candidate) => {
          const colorLabel = this.getOptionValue(candidate, colorDefinition.index);
          const colorKey = this.getOptionKey(colorLabel);
          if (!colorKey) return;

          let markerIndex = -1;

          const featuredMediaId = this.getVariantFeaturedMediaId(candidate);
          if (featuredMediaId && Object.prototype.hasOwnProperty.call(mediaIndexById, featuredMediaId)) {
            markerIndex = Number(mediaIndexById[featuredMediaId]);
          }

          if (markerIndex < 0) {
            const markerPosition = this.getVariantFeaturedMediaPosition(candidate);
            if (markerPosition > 0) {
              markerIndex = orderedItems.findIndex((item) => Number(item.dataset.mediaIndex || 0) >= markerPosition);
            }
          }

          if (markerIndex < 0) return;

          if (!Object.prototype.hasOwnProperty.call(indexByColorKey, colorKey)) {
            indexByColorKey[colorKey] = markerIndex;
            return;
          }
          if (markerIndex < indexByColorKey[colorKey]) indexByColorKey[colorKey] = markerIndex;
        });

        return Object.keys(indexByColorKey)
          .map((key) => ({ key, index: Number(indexByColorKey[key]) || 0 }))
          .filter((entry) => entry.index >= 0)
          .sort((a, b) => a.index - b.index);
      }

      getMediaRangeForVariant(mediaGallery, variant) {
        const orderedItems = this.getOrderedMediaItems(mediaGallery);
        if (!orderedItems.length) return null;

        const orderedMediaIds = orderedItems.map((item) => String(item.dataset.mediaId || '')).filter(Boolean);
        const orderedMediaIdSet = new Set(orderedMediaIds);
        const anchor = this.resolveVariantMediaAnchor(variant);
        if (!anchor || !anchor.id || !anchor.position) return null;

        const allAnchorPositions = this.getAllVariantAnchorPositions();
        const nextAnchorPosition = allAnchorPositions.find((position) => position > Number(anchor.position || 0)) || 0;

        const visibleIds = new Set();
        this.mediaData.forEach((media) => {
          if (!media || !media.id) return;
          const position = Number(media.position || 0);
          if (!Number.isFinite(position) || position < Number(anchor.position || 0)) return;
          if (nextAnchorPosition && position >= nextAnchorPosition) return;
          const mediaId = String(media.id || '');
          if (!mediaId || !orderedMediaIdSet.has(mediaId)) return;
          visibleIds.add(mediaId);
        });

        if (!visibleIds.size && orderedMediaIdSet.has(String(anchor.id))) {
          visibleIds.add(String(anchor.id));
        }

        return visibleIds.size ? visibleIds : null;
      }

      getActiveSliderMediaId(gallerySlider) {
        if (!gallerySlider || !gallerySlider.slides || !gallerySlider.slides.length) return '';
        const activeIndex = Number(gallerySlider.activeIndex) || 0;
        const activeSlide = gallerySlider.slides[activeIndex];
        return activeSlide ? String(activeSlide.dataset.mediaId || '') : '';
      }

      getActiveMediaIdFromGallery(mediaGallery, gallerySlider) {
        if (mediaGallery) {
          const activeVisible = mediaGallery.querySelector(
            '.product__media-item.swiper-slide-active[data-media-id]:not([hidden])'
          );
          if (activeVisible && activeVisible.dataset && activeVisible.dataset.mediaId) {
            return String(activeVisible.dataset.mediaId || '');
          }

          const activeAny = mediaGallery.querySelector('.product__media-item.swiper-slide-active[data-media-id]');
          if (activeAny && activeAny.dataset && activeAny.dataset.mediaId) {
            return String(activeAny.dataset.mediaId || '');
          }
        }
        return this.getActiveSliderMediaId(gallerySlider);
      }

      forceGalleryToMediaId(mediaGallery, mediaId) {
        if (!mediaGallery || !mediaId) return false;
        const targetId = String(mediaId || '').trim();
        if (!targetId) return false;

        const mediaItems = this.getMainMediaItems(mediaGallery);
        const targetItem = mediaItems.find((item) => String(item?.dataset?.mediaId || '') === targetId);
        if (!targetItem) return false;

        const mediaRecord = Array.isArray(this.mediaData)
          ? this.mediaData.find((media) => String(media?.id || '') === targetId)
          : null;
        const fallbackIndex = Number(targetItem.dataset.mediaIndex || 0);
        const targetPosition = Number(mediaRecord?.position || 0) || (Number.isFinite(fallbackIndex) ? fallbackIndex + 1 : 0);
        const canSetActiveMedia = targetPosition > 0 && typeof mediaGallery.setActiveMedia === 'function';
        if (canSetActiveMedia) {
          mediaGallery.setActiveMedia({
            featured_media: {
              id: targetId,
              position: targetPosition,
              media_type: 'image',
            },
          });
        }

        const gallerySlider = mediaGallery.sliderInstance && mediaGallery.sliderInstance.slider;
        if (!canSetActiveMedia && gallerySlider && typeof gallerySlider.slideTo === 'function') {
          const sliderSlides = Array.from(gallerySlider.slides || []);
          const targetSlideIndex = sliderSlides.findIndex(
            (slide) => String(slide?.dataset?.mediaId || '') === String(targetId)
          );
          if (targetSlideIndex >= 0) {
            gallerySlider.slideTo(targetSlideIndex, 0, false);
          }
        }

        this.syncThumbnailActiveState(mediaGallery, targetId);
        return true;
      }

      bindThumbnailClickOverrides(mediaGallery) {
        if (!mediaGallery) return;
        const thumbsRoot = mediaGallery.elements && mediaGallery.elements.thumbnails
          ? mediaGallery.elements.thumbnails
          : mediaGallery.querySelector('[id^="GalleryThumbnails"]');
        if (!thumbsRoot) return;

        const eventTypes = ['pointerdown', 'mousedown', 'touchstart', 'click'];
        if (thumbsRoot._pibulkThumbDelegateHandler) {
          eventTypes.forEach((eventType) => {
            thumbsRoot.removeEventListener(eventType, thumbsRoot._pibulkThumbDelegateHandler, true);
          });
        }

        const delegateHandler = (event) => {
          const target = event.target instanceof Element ? event.target : null;
          if (!target) return;
          const item = target.closest('.product__thumbs-item[data-target]');
          if (!item || !thumbsRoot.contains(item)) return;
          if (item.hidden || item.style.display === 'none') return;

          const targetMediaId = String(item.dataset.target || '').trim();
          if (!targetMediaId) return;

          this.activeFilteredMediaId = targetMediaId;
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

          this.forceGalleryToMediaId(mediaGallery, targetMediaId);
          window.requestAnimationFrame(() => this.forceGalleryToMediaId(mediaGallery, targetMediaId));
          window.setTimeout(() => this.forceGalleryToMediaId(mediaGallery, targetMediaId), 80);
          window.setTimeout(() => this.forceGalleryToMediaId(mediaGallery, targetMediaId), 220);
        };

        thumbsRoot._pibulkThumbDelegateHandler = delegateHandler;
        eventTypes.forEach((eventType) => {
          thumbsRoot.addEventListener(eventType, delegateHandler, { capture: true, passive: false });
        });
      }

      syncThumbnailActiveState(mediaGallery, mediaId) {
        if (!mediaGallery || !mediaId) return;
        const thumbItems = this.getThumbnailItems(mediaGallery);
        if (!thumbItems.length) return;

        thumbItems.forEach((item) => {
          item.classList.remove('swiper-slide-thumb-active');
          item.removeAttribute('aria-current');
        });

        const targetThumb =
          thumbItems.find((item) => String(item.dataset.target || '') === String(mediaId) && !item.hidden) ||
          thumbItems.find((item) => String(item.dataset.target || '') === String(mediaId));
        if (!targetThumb) return;

        targetThumb.classList.add('swiper-slide-thumb-active');
        targetThumb.setAttribute('aria-current', 'true');

        const thumbsSlider = mediaGallery.thumbsInstance && mediaGallery.thumbsInstance.slider;
        if (thumbsSlider && thumbsSlider.slides && thumbsSlider.slides.length && typeof thumbsSlider.slideTo === 'function') {
          const thumbIndex = Array.from(thumbsSlider.slides).findIndex(
            (slide) => String(slide?.dataset?.target || '') === String(mediaId)
          );
          if (thumbIndex >= 0) thumbsSlider.slideTo(thumbIndex, 0, false);
        }
      }

      applyVariantMediaRangeFilter(productInfo, variant) {
        if (!productInfo || !variant) return;

        const mediaGallery = productInfo.querySelector('media-gallery');
        if (!mediaGallery) return;
        const mediaItems = this.getMainMediaItems(mediaGallery);
        const thumbItems = this.getThumbnailItems(mediaGallery);

        mediaItems.forEach((item) => {
          item.hidden = false;
          item.style.display = '';
          item.setAttribute('aria-hidden', 'false');
        });

        thumbItems.forEach((item) => {
          item.hidden = false;
          item.style.display = '';
          item.setAttribute('aria-hidden', 'false');
        });

        const gallerySlider = mediaGallery.sliderInstance && mediaGallery.sliderInstance.slider;
        if (gallerySlider && typeof gallerySlider.update === 'function') {
          gallerySlider.update();
        }

        const thumbsSlider = mediaGallery.thumbsInstance && mediaGallery.thumbsInstance.slider;
        if (thumbsSlider && typeof thumbsSlider.update === 'function') {
          thumbsSlider.update();
        }
      }

      getVariantIdFromUrl() {
        try {
          const params = new URLSearchParams(window.location.search || '');
          return String(params.get('variant') || '').trim();
        } catch (error) {
          return '';
        }
      }

      resolveInitialVariantId() {
        const fromUrl = this.getVariantIdFromUrl();
        if (fromUrl && this.getVariantById(fromUrl)) return fromUrl;

        if (this.initialVariantId && this.getVariantById(this.initialVariantId)) return this.initialVariantId;

        const firstVariant = this.variants[0];
        return this.getVariantId(firstVariant);
      }

      getRelatedRecommendationsRoots() {
        return Array.from(document.querySelectorAll('.section--related-products product-recommendations'));
      }

      initRelatedProductsColorSync() {
        const recommendationRoots = this.getRelatedRecommendationsRoots();
        recommendationRoots.forEach((root) => {
          root.removeEventListener('recommendations:loaded', this.handleRelatedRecommendationsLoadedBound);
          root.addEventListener('recommendations:loaded', this.handleRelatedRecommendationsLoadedBound);
        });
      }

      teardownRelatedProductsColorSync() {
        const recommendationRoots = this.getRelatedRecommendationsRoots();
        recommendationRoots.forEach((root) => {
          root.removeEventListener('recommendations:loaded', this.handleRelatedRecommendationsLoadedBound);
        });
      }

      handleRelatedRecommendationsLoaded() {
        this.syncRelatedProductsToSelectedColor();
      }

      getSwatchColorKeyFromItem(item) {
        if (!item) return '';
        const tooltip = item.querySelector('.tooltip');
        const tooltipText = tooltip ? this.normalizeOptionText(tooltip.textContent || '') : '';
        if (tooltipText) return this.getOptionKey(tooltipText);

        const swatchLink = item.querySelector('a.swatch-color[title]');
        const title = swatchLink ? this.normalizeOptionText(swatchLink.getAttribute('title') || '') : '';
        if (!title) return '';
        const parts = title.split(' - ');
        const label = parts.length > 1 ? parts[parts.length - 1] : title;
        return this.getOptionKey(label);
      }

      getRelatedCardProductHandle(card) {
        if (!card) return '';
        const productLink = card.querySelector('.product-card__title a[href], .product-card__image-wrapper a[href], a[href]');
        const href = productLink ? String(productLink.getAttribute('href') || '').trim() : '';
        if (!href) return '';

        try {
          const parsed = new URL(href, window.location.origin);
          const parts = String(parsed.pathname || '')
            .split('/')
            .filter(Boolean);
          const productsIndex = parts.findIndex((part) => part === 'products');
          if (productsIndex < 0) return '';
          return String(parts[productsIndex + 1] || '').trim();
        } catch (error) {
          return '';
        }
      }

      getColorOptionIndexFromProductJson(productData) {
        const options = Array.isArray(productData && productData.options) ? productData.options : [];
        if (!options.length) return 0;

        const matchIndex = options.findIndex((optionName) => {
          const normalized = String(optionName || '').toLowerCase().replace(/\s+/g, ' ').trim();
          return /(^|[\s/_-])(color|colour)(s)?($|[\s/_-])/.test(normalized);
        });
        return matchIndex >= 0 ? matchIndex : 0;
      }

      getVariantOptionValueByIndex(variant, optionIndex) {
        if (!variant || optionIndex < 0) return '';
        if (Array.isArray(variant.options) && variant.options.length > optionIndex) {
          return this.normalizeOptionText(variant.options[optionIndex]);
        }
        return this.normalizeOptionText([variant.option1, variant.option2, variant.option3][optionIndex] || '');
      }

      async fetchRelatedProductJson(handle) {
        const safeHandle = String(handle || '').trim();
        if (!safeHandle) return null;

        if (Object.prototype.hasOwnProperty.call(this.relatedProductJsonCache, safeHandle)) {
          return this.relatedProductJsonCache[safeHandle];
        }
        if (this.relatedProductJsonRequests[safeHandle]) {
          return this.relatedProductJsonRequests[safeHandle];
        }

        const root = String(window.Shopify?.routes?.root || '/');
        const base = root.endsWith('/') ? root : `${root}/`;
        const url = `${base}products/${encodeURIComponent(safeHandle)}.js`;

        this.relatedProductJsonRequests[safeHandle] = fetch(url, {
          headers: { Accept: 'application/json' },
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            this.relatedProductJsonCache[safeHandle] = data || null;
            delete this.relatedProductJsonRequests[safeHandle];
            return this.relatedProductJsonCache[safeHandle];
          })
          .catch(() => {
            this.relatedProductJsonCache[safeHandle] = null;
            delete this.relatedProductJsonRequests[safeHandle];
            return null;
          });

        return this.relatedProductJsonRequests[safeHandle];
      }

      getRelatedVariantMatchByColor(productData, selectedColorKey) {
        if (!productData || !selectedColorKey) return null;
        const variants = Array.isArray(productData.variants) ? productData.variants : [];
        if (!variants.length) return null;

        const colorOptionIndex = this.getColorOptionIndexFromProductJson(productData);
        const matchedVariant = variants.find((variant) => {
          const optionValue = this.getVariantOptionValueByIndex(variant, colorOptionIndex);
          return this.getOptionKey(optionValue) === selectedColorKey;
        });
        if (!matchedVariant) return null;

        const featuredImage = matchedVariant.featured_image;
        let imageUrl = '';
        if (featuredImage && featuredImage.src) imageUrl = String(featuredImage.src);
        else if (typeof featuredImage === 'string' && featuredImage) imageUrl = String(featuredImage);
        else if (productData && productData.featured_image) imageUrl = String(productData.featured_image);
        if (!imageUrl) return null;

        const productImages = Array.isArray(productData.images)
          ? productData.images.map((image) => String(image || '').trim()).filter(Boolean)
          : [];
        const mainToken = this.getMediaUrlToken(imageUrl);
        const mainIndex = mainToken
          ? productImages.findIndex((image) => this.getMediaUrlToken(image) === mainToken)
          : -1;

        let secondaryImageUrl = '';
        if (mainIndex >= 0 && productImages.length > 1) {
          const anchorIndexes = [];
          const seenAnchorIndexes = {};

          variants.forEach((variant) => {
            const variantFeaturedImage = variant && variant.featured_image;
            let variantImageUrl = '';
            if (variantFeaturedImage && variantFeaturedImage.src) variantImageUrl = String(variantFeaturedImage.src);
            else if (typeof variantFeaturedImage === 'string' && variantFeaturedImage) {
              variantImageUrl = String(variantFeaturedImage);
            }
            if (!variantImageUrl) return;

            const token = this.getMediaUrlToken(variantImageUrl);
            if (!token) return;

            const imageIndex = productImages.findIndex((image) => this.getMediaUrlToken(image) === token);
            if (imageIndex < 0 || seenAnchorIndexes[imageIndex]) return;
            seenAnchorIndexes[imageIndex] = true;
            anchorIndexes.push(imageIndex);
          });

          anchorIndexes.sort((a, b) => a - b);
          const nextAnchorIndex = anchorIndexes.find((index) => index > mainIndex);
          const endExclusive = Number.isFinite(nextAnchorIndex) ? nextAnchorIndex : productImages.length;
          const sameColorNextIndex = mainIndex + 1;
          if (sameColorNextIndex < endExclusive && productImages[sameColorNextIndex]) {
            secondaryImageUrl = productImages[sameColorNextIndex];
          } else if (productImages[mainIndex + 1]) {
            secondaryImageUrl = productImages[mainIndex + 1];
          }
        }

        const productUrl = String(productData.url || '').trim();
        const variantId = matchedVariant && matchedVariant.id != null ? String(matchedVariant.id) : '';
        const variantUrl = productUrl && variantId ? `${productUrl}?variant=${encodeURIComponent(variantId)}` : '';

        return { imageUrl, secondaryImageUrl, variantUrl };
      }

      setRelatedCardSecondaryImage(card, secondaryImageUrl) {
        if (!card) return;
        const imageWrapper = card.querySelector('.product-card__image-wrapper');
        const secondaryMedia = card.querySelector('.product-card__image--second');
        const secondaryImage = secondaryMedia ? secondaryMedia.querySelector('img') : null;
        if (!imageWrapper || !secondaryMedia || !secondaryImage) return;

        const nextUrl = String(secondaryImageUrl || '').trim();
        if (!nextUrl) {
          secondaryMedia.style.display = 'none';
          imageWrapper.classList.add('product-card__image-wrapper--main-only');
          return;
        }

        secondaryMedia.style.display = '';
        imageWrapper.classList.remove('product-card__image-wrapper--main-only');
        secondaryImage.src = nextUrl;
        secondaryImage.removeAttribute('srcset');
      }

      applyRelatedCardMainImage(card, cardImage, imageUrl, secondaryImageUrl) {
        if (!cardImage || !imageUrl) return;
        cardImage.src = imageUrl;
        cardImage.removeAttribute('srcset');
        this.setRelatedCardSecondaryImage(card, secondaryImageUrl);
      }

      applyRelatedCardMainImageFromSwatch(card, cardImage, swatchImage) {
        if (!cardImage || !swatchImage) return;
        if (swatchImage.currentSrc) cardImage.src = swatchImage.currentSrc;
        else if (swatchImage.src) cardImage.src = swatchImage.src;
        if (swatchImage.srcset) cardImage.srcset = swatchImage.srcset;
        if (swatchImage.sizes) cardImage.sizes = swatchImage.sizes;
        if (swatchImage.alt) cardImage.alt = swatchImage.alt;
        this.setRelatedCardSecondaryImage(card, '');
      }

      applyRelatedCardVariantUrl(card, variantUrl) {
        if (!card) return;
        const href = String(variantUrl || '').trim();
        if (!href) return;

        const linkSelectors = [
          '.product-card__image-wrapper > a[href]',
          '.product-card__title a[href]',
          '.product-card__atc[href]',
          '.swatch-more a[href]',
        ];

        linkSelectors.forEach((selector) => {
          Array.from(card.querySelectorAll(selector)).forEach((link) => {
            link.setAttribute('href', href);
          });
        });
      }

      async syncRelatedProductsToSelectedColor() {
        const selectedColorContext = this.getSelectedColorContext();
        const selectedColorKey = selectedColorContext && selectedColorContext.colorKey ? selectedColorContext.colorKey : '';
        if (!selectedColorKey) return;
        const syncToken = (this.relatedColorSyncToken || 0) + 1;
        this.relatedColorSyncToken = syncToken;

        const productCards = Array.from(document.querySelectorAll('.section--related-products .product-card'));
        if (!productCards.length) return;

        await Promise.all(
          productCards.map(async (card) => {
            if (syncToken !== this.relatedColorSyncToken) return;
            const cardImage = card.querySelector('.product-card__image--main img');
            if (!cardImage) return;

            const swatchItems = Array.from(card.querySelectorAll('.product-card__swatches .swatch-item'));
            if (swatchItems.length) {
              swatchItems.forEach((item) => item.removeAttribute('aria-selected'));
              const matchedSwatch = swatchItems.find(
                (item) => this.getSwatchColorKeyFromItem(item) === selectedColorKey
              );

              if (matchedSwatch) {
                matchedSwatch.setAttribute('aria-selected', 'true');
                const swatchLink = matchedSwatch.querySelector('a.swatch-color[href]');
                const swatchHref = swatchLink ? String(swatchLink.getAttribute('href') || '').trim() : '';
                if (swatchHref) this.applyRelatedCardVariantUrl(card, swatchHref);

                const swatchImage = matchedSwatch.querySelector('.color-swatch--variant-image');
                if (swatchImage) {
                  this.applyRelatedCardMainImageFromSwatch(card, cardImage, swatchImage);
                }
              }
            }

            const handle = this.getRelatedCardProductHandle(card);
            if (!handle) return;
            const productData = await this.fetchRelatedProductJson(handle);
            if (syncToken !== this.relatedColorSyncToken) return;
            const match = this.getRelatedVariantMatchByColor(productData, selectedColorKey);
            if (!match || !match.imageUrl) return;
            this.applyRelatedCardMainImage(card, cardImage, match.imageUrl, match.secondaryImageUrl);
            if (match.variantUrl) this.applyRelatedCardVariantUrl(card, match.variantUrl);
          })
        );
      }

      getVariantStockRecord(variant) {
        const variantId = this.getVariantId(variant);
        if (!variantId) return null;
        return this.variantStockMap && this.variantStockMap[variantId] ? this.variantStockMap[variantId] : null;
      }

      getOptionValue(variant, index) {
        if (!variant || index < 0) return '';
        return this.normalizeOptionText([variant.option1, variant.option2, variant.option3][index] || '');
      }

      getInitialSelectedOptions() {
        const initialVariant = this.getVariantById(this.initialVariantId) || this.variants[0];
        const selected = {};

        this.optionDefinitions.forEach((definition) => {
          const fromVariant = this.getOptionValue(initialVariant, definition.index);
          const fromVariantKey = this.getOptionKey(fromVariant);
          const matchedValue = definition.values.find((item) => this.getOptionKey(item.label) === fromVariantKey);
          selected[definition.index] = matchedValue ? matchedValue.label : definition.values[0].label;
        });

        return selected;
      }

      getCandidateVariantsForDefinition(definitionIndex, selectedOptionsOverride, requireSellable) {
        const selectedOptions = selectedOptionsOverride || this.selectedOptions || {};
        let candidates = this.variants.filter((variant) =>
          this.optionDefinitions.every((check) => {
            if (check.index === definitionIndex) return true;

            const selectedKey = this.getOptionKey(selectedOptions[check.index]);
            if (!selectedKey) return true;
            return this.getOptionKey(this.getOptionValue(variant, check.index)) === selectedKey;
          })
        );

        if (requireSellable) {
          candidates = candidates.filter((variant) => this.isVariantSellable(variant));
        }

        return candidates;
      }

      getRenderableDefinitionValues(definition, selectedOptionsOverride) {
        if (!definition || !Array.isArray(definition.values)) return [];
        if (this.showOutOfStockVariants) return definition.values;

        const candidates = this.getCandidateVariantsForDefinition(
          definition.index,
          selectedOptionsOverride || this.selectedOptions,
          true
        );
        if (!candidates.length) return [];

        return definition.values.filter((optionValue) => {
          const optionKey = this.getOptionKey(optionValue.label);
          return candidates.some(
            (variant) => this.getOptionKey(this.getOptionValue(variant, definition.index)) === optionKey
          );
        });
      }

      normalizeSelectedOptions(changedIndex) {
        if (!this.optionDefinitions.length) return false;

        const normalized = { ...this.selectedOptions };
        const changed = Number.parseInt(changedIndex, 10);
        let didChange = false;

        this.optionDefinitions.forEach((definition) => {
          if (definition.index === changed) return;

          const currentKey = this.getOptionKey(normalized[definition.index]);
          const candidates = this.getCandidateVariantsForDefinition(
            definition.index,
            normalized,
            !this.showOutOfStockVariants
          );

          if (!candidates.length) return;

          const stillValid = candidates.some(
            (variant) => this.getOptionKey(this.getOptionValue(variant, definition.index)) === currentKey
          );
          if (stillValid) return;

          const nextValue = this.getRenderableDefinitionValues(definition, normalized).find((optionValue) => {
            const optionKey = this.getOptionKey(optionValue.label);
            return candidates.some(
              (variant) => this.getOptionKey(this.getOptionValue(variant, definition.index)) === optionKey
            );
          });

          if (nextValue) {
            normalized[definition.index] = nextValue.label;
            didChange = true;
          }
        });

        this.selectedOptions = normalized;
        return didChange;
      }

      getFilteredVariants() {
        let list = this.variants.slice();

        this.optionDefinitions.forEach((definition) => {
          const selectedValue = this.selectedOptions[definition.index];
          const selectedKey = this.getOptionKey(selectedValue);
          if (!selectedKey) return;
          list = list.filter((variant) => this.getOptionKey(this.getOptionValue(variant, definition.index)) === selectedKey);
        });

        if (this.sizeOrder.length) {
          const rank = {};
          this.sizeOrder.forEach((value, index) => {
            rank[this.getOptionKey(value)] = index;
          });

          list.sort((a, b) => {
            const aSize = this.getOptionKey(this.getOptionValue(a, this.sizeIndex));
            const bSize = this.getOptionKey(this.getOptionValue(b, this.sizeIndex));
            const aRank = Object.prototype.hasOwnProperty.call(rank, aSize) ? rank[aSize] : 999;
            const bRank = Object.prototype.hasOwnProperty.call(rank, bSize) ? rank[bSize] : 999;
            return aRank - bRank;
          });
        }

        return list;
      }

      getVariantForSync(variants) {
        if (!Array.isArray(variants) || !variants.length) return null;

        const preferredSizeKey = this.getPreferredSizeKey();

        if (this.lastInteractedVariantId) {
          const lastMatched = variants.find((variant) => this.getVariantId(variant) === this.lastInteractedVariantId);
          if (lastMatched) return lastMatched;
        }

        const quantityVariant = variants.find((variant) => Number(this.quantities[this.getVariantId(variant)] || 0) > 0);
        if (quantityVariant) return quantityVariant;

        const preferredSellableMatch = this.findBestVariantMatch(variants, preferredSizeKey, true);
        if (preferredSellableMatch) return preferredSellableMatch;

        if (this.initialVariantId) {
          const initialMatched = variants.find((variant) => this.getVariantId(variant) === this.initialVariantId);
          if (initialMatched && this.isVariantSellable(initialMatched)) return initialMatched;
        }

        const anySellableMatch = this.findBestVariantMatch(variants, '', true);
        if (anySellableMatch) return anySellableMatch;

        const preferredMatch = this.findBestVariantMatch(variants, preferredSizeKey, false);
        if (preferredMatch) return preferredMatch;

        return variants[0];
      }

      getPreferredSizeKey() {
        if (this.sizeIndex < 0) return '';

        const preferredVariant =
          this.getVariantById(this.lastInteractedVariantId) ||
          this.getVariantById(this.initialVariantId) ||
          null;
        if (!preferredVariant) return '';

        return this.getOptionKey(this.getOptionValue(preferredVariant, this.sizeIndex));
      }

      findBestVariantMatch(variants, preferredSizeKey, requireSellable) {
        if (!Array.isArray(variants) || !variants.length) return null;

        const filtered = requireSellable ? variants.filter((variant) => this.isVariantSellable(variant)) : variants.slice();
        if (!filtered.length) return null;

        if (preferredSizeKey) {
          const sameSizeMatch = filtered.find(
            (variant) => this.getOptionKey(this.getOptionValue(variant, this.sizeIndex)) === preferredSizeKey
          );
          if (sameSizeMatch) return sameSizeMatch;
        }

        return filtered[0] || null;
      }

      syncMainVariantFromSelection(variantsOverride) {
        const variants = Array.isArray(variantsOverride) ? variantsOverride : this.getFilteredVariants();
        const targetVariant = this.getVariantForSync(variants);
        if (!targetVariant) return;
        this.lastInteractedVariantId = this.getVariantId(targetVariant);
        this.syncMainProductVariant(targetVariant);
        this.syncRelatedProductsToSelectedColor();
      }

      getColorStyle(value) {
        const color = String(value || '').trim().toLowerCase();
        const named = {
          black: '#101010',
          white: '#f7f7f7',
          red: '#e3242b',
          blue: '#0068c9',
          navy: '#151c6d',
          green: '#008a4b',
          yellow: '#f1c343',
          orange: '#e78524',
          grey: '#8f8f8f',
          gray: '#8f8f8f',
          purple: '#775d9f',
          pink: '#d7477b',
          brown: '#70412b',
          burgundy: '#7a1732',
          bottle: '#0f5b48',
          'bottle green': '#0f5b48',
          khaki: '#808160',
          gold: '#cf9f2f',
          silver: '#b6bcc4',
        };

        if (named[color]) return named[color];
        if (color.indexOf('#') === 0) return color;

        let hash = 0;
        for (let index = 0; index < color.length; index += 1) {
          hash = color.charCodeAt(index) + ((hash << 5) - hash);
        }
        const hue = Math.abs(hash) % 360;
        return `hsl(${hue} 62% 46%)`;
      }

      getSwatchBackground(value) {
        if (value.image) return `url(${value.image})`;
        if (value.color) return value.color;
        return this.getColorStyle(value.label);
      }

      formatMoney(cents) {
        const amount = Number(cents || 0);
        if (window.FoxTheme && window.FoxTheme.Currency && typeof window.FoxTheme.Currency.formatMoney === 'function') {
          return window.FoxTheme.Currency.formatMoney(amount, window.FoxTheme?.settings?.moneyFormat);
        }
        return `£${(amount / 100).toFixed(2)}`;
      }

      normalizeVolumeDiscount(discount) {
        if (!discount || typeof discount !== 'object') return null;

        const thresholdType = String(discount.threshold_type || '').toUpperCase().trim() || 'QUANTITY';
        const rewardType = String(discount.reward_type || '').toUpperCase().trim() || 'PERCENTAGE';
        const thresholdsRaw = Array.isArray(discount.thresholds) ? discount.thresholds : [];
        const rewardsRaw = Array.isArray(discount.rewards) ? discount.rewards : [];
        const messagesRaw = Array.isArray(discount.messages) ? discount.messages : [];

        const maxLength = Math.min(thresholdsRaw.length, rewardsRaw.length);
        if (maxLength < 1) return null;

        const tiers = [];
        for (let index = 0; index < maxLength; index += 1) {
          const threshold = Number.parseFloat(thresholdsRaw[index]);
          const reward = Number.parseFloat(rewardsRaw[index]);
          if (!Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(reward) || reward < 0) continue;
          tiers.push({
            threshold,
            reward,
            message: String(messagesRaw[index] || '').trim(),
          });
        }

        if (!tiers.length) return null;
        tiers.sort((a, b) => a.threshold - b.threshold);

        if (thresholdType === 'QUANTITY') {
          const hasOneItemTier = tiers.some((tier) => Number(tier.threshold) === 1);
          if (!hasOneItemTier) {
            tiers.unshift({
              threshold: 1,
              reward: 0,
              message: '',
            });
          }
        }

        return {
          title: String(discount.discount_title || '').trim(),
          thresholdType,
          rewardType,
          tiers,
        };
      }

      normalizeGwpDiscount(discount) {
        if (!discount || typeof discount !== 'object') return null;
        const thresholdType = String(discount.threshold_type || '').toUpperCase().trim() || 'QUANTITY';
        const thresholdCollectionUrl = String(discount.threshold_collection_url || '').trim();
        const thresholdCollectionTitle = String(discount.threshold_collection_title || '').trim();
        const tiersRaw = Array.isArray(discount.tiers) ? discount.tiers : [];
        const tiers = [];

        tiersRaw.forEach((tier) => {
          const threshold = Number.parseFloat(tier && tier.threshold);
          if (!Number.isFinite(threshold) || threshold <= 0) return;
          const rewardQuantity = Math.max(0, Number.parseInt(tier && tier.reward_quantity, 10) || 0);
          const giftIndexes = Array.isArray(tier && tier.gift_indexes)
            ? tier.gift_indexes
                .map((idx) => Number.parseInt(idx, 10))
                .filter((idx) => Number.isFinite(idx))
            : [];
          const giftsRaw = Array.isArray(tier && tier.gifts) ? tier.gifts : [];
          const gifts = giftsRaw
            .map((gift) => ({
              title: String((gift && gift.title) || '').trim(),
              url: String((gift && gift.url) || '').trim(),
              image: String((gift && gift.image) || '').trim(),
              variantId: String((gift && gift.variant_id) || '').trim(),
              variants: Array.isArray(gift && gift.variants)
                ? gift.variants
                    .map((variant) => ({
                      id: String((variant && variant.id) || '').trim(),
                      title: String((variant && variant.title) || '').trim(),
                      available: !!(variant && variant.available),
                    }))
                    .filter((variant) => variant.id)
                : [],
            }))
            .filter((gift) => gift.title || gift.image || gift.url || gift.variantId);

          tiers.push({
            threshold,
            message: String((tier && tier.message) || '').trim(),
            rewardQuantity,
            giftIndexes,
            gifts,
          });
        });

        if (!tiers.length) return null;
        tiers.sort((a, b) => a.threshold - b.threshold);

        return {
          title: String(discount.discount_title || '').trim(),
          thresholdType,
          thresholdCollectionUrl,
          thresholdCollectionTitle,
          tiers,
        };
      }

      getCartQuantityForProductVariants() {
        if (!Array.isArray(this.variants) || !this.variants.length) return 0;
        return this.variants.reduce((total, variant) => {
          const variantId = this.getVariantId(variant);
          if (!variantId) return total;
          return total + this.getCartQuantityForVariant(variantId);
        }, 0);
      }

      getCartAmountForProductVariantsCents() {
        if (!Array.isArray(this.variants) || !this.variants.length) return 0;
        return this.variants.reduce((total, variant) => {
          const variantId = this.getVariantId(variant);
          if (!variantId) return total;
          const quantity = this.getCartQuantityForVariant(variantId);
          if (quantity <= 0) return total;
          const price = Number(variant && variant.price != null ? variant.price : 0) || 0;
          return total + price * quantity;
        }, 0);
      }

      getSelectedAmountCents() {
        return Object.keys(this.quantities).reduce((total, variantId) => {
          const quantity = Number(this.quantities[variantId] || 0);
          if (quantity <= 0) return total;
          const variant = this.getVariantById(variantId);
          if (!variant) return total;
          const price = Number(variant && variant.price != null ? variant.price : 0) || 0;
          return total + price * quantity;
        }, 0);
      }

      getVolumeTierActiveIndex(discount, effectiveQuantity, effectiveAmountCents) {
        if (!discount || !Array.isArray(discount.tiers) || !discount.tiers.length) return -1;

        const basis =
          discount.thresholdType === 'AMOUNT'
            ? Number(effectiveAmountCents || 0)
            : Number(effectiveQuantity || 0);

        let activeIndex = -1;
        discount.tiers.forEach((tier, index) => {
          if (basis >= tier.threshold) activeIndex = index;
        });
        return activeIndex;
      }

      getVolumeDiscountState() {
        const discount = this.volumeDiscount;
        if (!discount || !Array.isArray(discount.tiers) || !discount.tiers.length) {
          return {
            discount: null,
            activeIndex: -1,
            activeTier: null,
            effectiveQuantity: 0,
            effectiveAmountCents: 0,
          };
        }

        const effectiveQuantity = this.getTotalQuantity() + this.getCartQuantityForProductVariants();
        const effectiveAmountCents = this.getSelectedAmountCents() + this.getCartAmountForProductVariantsCents();
        const activeIndex = this.getVolumeTierActiveIndex(discount, effectiveQuantity, effectiveAmountCents);
        const activeTier = activeIndex >= 0 ? discount.tiers[activeIndex] : null;

        return {
          discount,
          activeIndex,
          activeTier,
          effectiveQuantity,
          effectiveAmountCents,
        };
      }

      getGwpState() {
        const discount = this.gwpDiscount;
        if (!discount || !Array.isArray(discount.tiers) || !discount.tiers.length) {
          return {
            discount: null,
            achievedIndex: -1,
            effectiveQuantity: 0,
            effectiveAmountCents: 0,
          };
        }

        const effectiveQuantity = this.getTotalQuantity() + this.getCartQuantityForProductVariants();
        const effectiveAmountCents = this.getSelectedAmountCents() + this.getCartAmountForProductVariantsCents();
        const basis =
          discount.thresholdType === 'AMOUNT'
            ? Number(effectiveAmountCents || 0)
            : Number(effectiveQuantity || 0);

        let achievedIndex = -1;
        discount.tiers.forEach((tier, index) => {
          if (basis >= Number(tier.threshold || 0)) achievedIndex = index;
        });

        return {
          discount,
          achievedIndex,
          effectiveQuantity,
          effectiveAmountCents,
        };
      }

      renderGwpTiers() {
        if (!this.gwpElement) return;
        const state = this.getGwpState();
        const discount = state.discount;
        if (!discount || !Array.isArray(discount.tiers) || !discount.tiers.length) {
          this.gwpElement.hidden = true;
          this.gwpElement.innerHTML = '';
          return;
        }

        const cards = discount.tiers
          .map((tier, index) => {
            const achieved = index <= state.achievedIndex;
            const storedState = this.gwpUiState && this.gwpUiState[index] ? this.gwpUiState[index] : null;
            const thresholdValue = Math.max(0, Math.round(Number(tier.threshold || 0)));
            const thresholdSuffix = '';
            const thresholdLabel =
              discount.thresholdType === 'AMOUNT'
                ? `when you spend ${this.formatMoney(Number(tier.threshold || 0))}+`
                : `when you purchase ${thresholdValue}+ items`;
            const gifts = Array.isArray(tier.gifts) ? tier.gifts : [];
            const primaryGift = gifts[0] || null;
            const giftOptionsSource = gifts.length ? gifts : primaryGift ? [primaryGift] : [];
            const selectedGiftIndexRaw = Number.parseInt(storedState && storedState.giftIndex, 10);
            const selectedGiftIndex =
              Number.isFinite(selectedGiftIndexRaw) &&
              selectedGiftIndexRaw >= 0 &&
              selectedGiftIndexRaw < giftOptionsSource.length
                ? selectedGiftIndexRaw
                : 0;
            const selectedGift = giftOptionsSource[selectedGiftIndex] || primaryGift;
            const selectedGiftTitleRaw = (selectedGift && selectedGift.title) || 'Free gift';
            const selectedGiftTitle = this.escapeHtml(selectedGiftTitleRaw);
            const selectedGiftTitleAlt = this.escapeHtml(selectedGiftTitleRaw);
            const imageMarkup =
              selectedGift && selectedGift.image
                ? `<img class="pibulk__gwp-gift-image" src="${this.escapeHtml(selectedGift.image)}" alt="${selectedGiftTitleAlt}" loading="lazy" width="80" height="80">`
                : '';
            const selectedGiftVariantsRaw = Array.isArray(selectedGift && selectedGift.variants)
              ? selectedGift.variants
              : [];
            const selectedGiftVariants =
              selectedGiftVariantsRaw.filter((variant) => variant && variant.available) || [];
            const effectiveGiftVariants = selectedGiftVariants.length
              ? selectedGiftVariants
              : selectedGiftVariantsRaw;
            const fallbackVariantId = String(
              (selectedGift && selectedGift.variantId) ||
                (effectiveGiftVariants[0] && effectiveGiftVariants[0].id) ||
                ''
            ).trim();
            const selectedVariantId = (() => {
              const storedVariantId = String((storedState && storedState.variantId) || '').trim();
              if (
                storedVariantId &&
                effectiveGiftVariants.some((variant) => String(variant.id || '').trim() === storedVariantId)
              ) {
                return storedVariantId;
              }
              return fallbackVariantId;
            })();
            const freebieItemMarkup =
              discount.thresholdCollectionUrl && String(discount.thresholdCollectionUrl).trim()
                ? `<a class="pibulk__gwp-freebie-link" href="${this.escapeHtml(
                    String(discount.thresholdCollectionUrl).trim()
                  )}">${selectedGiftTitle}</a>`
                : `<span class="pibulk__gwp-freebie-link">${selectedGiftTitle}</span>`;
            const unlockedText = achieved
              ? `<p class="pibulk__gwp-unlocked">
                  <span>Unlocked</span>
                </p>`
              : `<p class="pibulk__gwp-unlocked pibulk__gwp-unlocked--locked">
                  <span>Locked</span>
                </p>`;

            return `
              <article class="pibulk__gwp-card${achieved ? ' is-achieved' : ''}" data-gwp-tier-index="${index}">
                ${unlockedText}
                <div class="pibulk__gwp-row">
                  <div class="pibulk__gwp-image-cell">${imageMarkup}</div>
                  <div class="pibulk__gwp-copy-cell">
                    <div class="pibulk__gwp-summary">
                      <p class="pibulk__gwp-freebie">Get FREE ${freebieItemMarkup}</p>
                      <p class="pibulk__gwp-threshold">${this.escapeHtml(thresholdLabel)}${thresholdSuffix}</p>
                    </div>
                  </div>
                </div>
              </article>
            `;
          })
          .join('');

        this.gwpElement.hidden = false;
        this.gwpElement.innerHTML = `
          <div class="pibulk__gwp-carousel">
            <div class="pibulk__gwp-head">
              <h4 class="pibulk__gwp-title">Free Gift with Purchase</h4>
              <div class="pibulk__gwp-controls${discount.tiers.length > 1 ? '' : ' is-hidden'}">
                <button type="button" class="pibulk__gwp-nav pibulk__gwp-nav--prev" aria-label="Previous gift tier">‹</button>
                <span class="pibulk__gwp-count">1/${discount.tiers.length}</span>
                <button type="button" class="pibulk__gwp-nav pibulk__gwp-nav--next" aria-label="Next gift tier">›</button>
              </div>
            </div>
            <div class="pibulk__gwp-track" role="list">
              ${cards}
            </div>
          </div>
        `;
        this.initGwpCarousel();
      }

      initGwpCarousel() {
        if (!this.gwpElement) return;
        const track = this.gwpElement.querySelector('.pibulk__gwp-track');
        const prevButton = this.gwpElement.querySelector('.pibulk__gwp-nav--prev');
        const nextButton = this.gwpElement.querySelector('.pibulk__gwp-nav--next');
        const countElement = this.gwpElement.querySelector('.pibulk__gwp-count');
        const cards = track ? Array.from(track.querySelectorAll('.pibulk__gwp-card')) : [];
        if (!track || cards.length < 2) return;

        const updateState = () => {
          let activeIndex = 0;
          let bestDelta = Number.POSITIVE_INFINITY;
          cards.forEach((card, index) => {
            const delta = Math.abs(card.offsetLeft - track.scrollLeft);
            if (delta < bestDelta) {
              bestDelta = delta;
              activeIndex = index;
            }
          });

          if (countElement) countElement.textContent = `${activeIndex + 1}/${cards.length}`;
          if (prevButton) prevButton.disabled = activeIndex <= 0;
          if (nextButton) nextButton.disabled = activeIndex >= cards.length - 1;
        };

        const scrollToIndex = (index) => {
          const target = cards[Math.max(0, Math.min(cards.length - 1, index))];
          if (!target) return;
          track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        };

        if (prevButton) {
          prevButton.addEventListener('click', () => {
            let activeIndex = 0;
            let bestDelta = Number.POSITIVE_INFINITY;
            cards.forEach((card, index) => {
              const delta = Math.abs(card.offsetLeft - track.scrollLeft);
              if (delta < bestDelta) {
                bestDelta = delta;
                activeIndex = index;
              }
            });
            scrollToIndex(activeIndex - 1);
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            let activeIndex = 0;
            let bestDelta = Number.POSITIVE_INFINITY;
            cards.forEach((card, index) => {
              const delta = Math.abs(card.offsetLeft - track.scrollLeft);
              if (delta < bestDelta) {
                bestDelta = delta;
                activeIndex = index;
              }
            });
            scrollToIndex(activeIndex + 1);
          });
        }

        track.addEventListener('scroll', () => {
          window.requestAnimationFrame(updateState);
        }, { passive: true });

        window.requestAnimationFrame(updateState);
      }

      persistGwpCardState(card) {
        if (!card) return;
        const index = Number.parseInt(card.getAttribute('data-gwp-tier-index') || '-1', 10);
        if (!Number.isFinite(index) || index < 0) return;
        const productSelect = card.querySelector('[data-gwp-product-select]');
        const variantSelect = card.querySelector('[data-gwp-variant-select]');
        const qtyInput = card.querySelector('[data-gwp-qty-input]');
        const configure = card.querySelector('.pibulk__gwp-configure');
        if (!this.gwpUiState) this.gwpUiState = {};
        this.gwpUiState[index] = {
          open: !!(configure && !configure.hasAttribute('hidden')),
          giftIndex: productSelect ? Number.parseInt(productSelect.value || '0', 10) || 0 : 0,
          variantId: variantSelect ? String(variantSelect.value || '').trim() : '',
          qty: qtyInput ? Number.parseInt(qtyInput.value || '1', 10) || 1 : 1,
        };
      }

      adjustGwpQty(button, delta) {
        const card = button ? button.closest('.pibulk__gwp-card') : null;
        if (!card) return;
        const qtyInput = card.querySelector('[data-gwp-qty-input]');
        if (!qtyInput || qtyInput.disabled) return;
        const min = Math.max(1, Number.parseInt(qtyInput.getAttribute('min') || '1', 10) || 1);
        const max = Math.max(min, Number.parseInt(qtyInput.getAttribute('max') || String(min), 10) || min);
        const current = Number.parseInt(qtyInput.value || String(min), 10) || min;
        const next = Math.max(min, Math.min(max, current + Number(delta || 0)));
        qtyInput.value = String(next);
        const decButton = card.querySelector('[data-gwp-qty-dec]');
        const incButton = card.querySelector('[data-gwp-qty-inc]');
        if (decButton) decButton.disabled = next <= min;
        if (incButton) incButton.disabled = next >= max;
        this.persistGwpCardState(card);
      }

      toggleGwpConfigure(button) {
        const card = button ? button.closest('.pibulk__gwp-card') : null;
        if (!card) return;
        const summary = card.querySelector('.pibulk__gwp-summary');
        const configure = card.querySelector('.pibulk__gwp-configure');
        if (!summary || !configure) return;
        const isHidden = configure.hasAttribute('hidden');
        if (isHidden) {
          configure.removeAttribute('hidden');
          summary.setAttribute('hidden', 'hidden');
        } else {
          configure.setAttribute('hidden', 'hidden');
          summary.removeAttribute('hidden');
        }
        this.persistGwpCardState(card);
      }

      async handleGwpAddToCart(button) {
        if (!button || button.disabled) return;
        const card = button.closest('.pibulk__gwp-card');
        if (!card) return;
        this.persistGwpCardState(card);
        const variantSelect = card.querySelector('[data-gwp-variant-select]');
        const qtyInput = card.querySelector('[data-gwp-qty-input]');
        const variantId = variantSelect
          ? String(variantSelect.value || '').trim()
          : String(button.getAttribute('data-gwp-default-variant-id') || '').trim();
        const quantity = qtyInput ? Math.max(1, Number.parseInt(qtyInput.value || '1', 10) || 1) : 1;
        if (!variantId) return;

        const previousText = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding...';
        this.setStatus('', '');
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
          });
          if (!response.ok) throw new Error('Unable to add free gift.');
          await response.json();
          await this.notifyCartUpdated();
          this.persistGwpCardState(card);
        } catch (error) {
          this.setStatus(error.message || 'Unable to add free gift.', 'is-error');
        } finally {
          button.disabled = false;
          button.textContent = previousText;
        }
      }

      getDisplayPriceCents(basePriceCents, discountState) {
        const base = Number(basePriceCents || 0) || 0;
        if (!discountState || !discountState.discount || !discountState.activeTier) return base;
        return this.getTierUnitPriceCents(base, discountState.discount.rewardType, discountState.activeTier.reward);
      }

      getTierUnitPriceCents(basePriceCents, rewardType, rewardValue) {
        const base = Number(basePriceCents || 0);
        const reward = Number(rewardValue || 0);
        if (!Number.isFinite(base) || base <= 0) return 0;
        if (!Number.isFinite(reward) || reward <= 0) return Math.max(0, Math.round(base));

        if (rewardType === 'PERCENTAGE') {
          return Math.max(0, Math.round(base * (1 - reward / 100)));
        }
        if (rewardType === 'FIXED_AMOUNT') {
          return Math.max(0, Math.round(base - reward));
        }
        return Math.max(0, Math.round(base));
      }

      getTierThresholdLabel(discount, thresholdValue) {
        if (!discount) return '';
        if (discount.thresholdType === 'AMOUNT') {
          return `${this.formatMoney(Number(thresholdValue || 0))}+`;
        }
        const qty = Math.max(0, Math.round(Number(thresholdValue || 0)));
        if (qty <= 1) return '1 item';
        return `${qty}+ items`;
      }

      getTierBadgeLabel(discount, tier) {
        if (!discount || !tier) return '';
        if (discount.rewardType === 'PERCENTAGE') {
          return `SAVE ${Math.round(Number(tier.reward || 0))}%`;
        }
        if (discount.rewardType === 'FIXED_AMOUNT') {
          return `SAVE ${this.formatMoney(Number(tier.reward || 0))}`;
        }
        return '';
      }

      renderVolumeDiscountTiers(variantsOverride, discountStateOverride) {
        if (!this.volumeTiersElement) return;
        const state = discountStateOverride || this.getVolumeDiscountState();
        const discount = state.discount;
        if (!discount || !Array.isArray(discount.tiers) || !discount.tiers.length) {
          this.volumeTiersElement.hidden = true;
          this.volumeTiersElement.innerHTML = '';
          return;
        }

        const filteredVariants = Array.isArray(variantsOverride) ? variantsOverride : this.getFilteredVariants();
        const priceVariant = this.getVariantForSync(filteredVariants) || filteredVariants[0] || this.variants[0] || null;
        if (!priceVariant) {
          this.volumeTiersElement.hidden = true;
          this.volumeTiersElement.innerHTML = '';
          return;
        }

        const basePriceCents = Number(priceVariant.price || 0) || 0;
        const activeIndex = Number(state.activeIndex);
        const hasSelection =
          Number(state && state.effectiveQuantity != null ? state.effectiveQuantity : 0) > 0 ||
          Number(state && state.effectiveAmountCents != null ? state.effectiveAmountCents : 0) > 0;
        this.volumeTiersElement.hidden = false;
        const rowsHtml = discount.tiers
          .map((tier, index) => {
            const isActive = index === activeIndex;
            const tierPriceCents = this.getTierUnitPriceCents(basePriceCents, discount.rewardType, tier.reward);
            const badgeLabel = this.getTierBadgeLabel(discount, tier);
            const hasBadge = Number(tier.reward || 0) > 0 && Boolean(badgeLabel);
            const thresholdLabel = this.getTierThresholdLabel(discount, tier.threshold);

            return `
              <li class="pibulk__tier-row${isActive ? ' is-active' : ''}">
                <span class="pibulk__tier-price">${this.escapeHtml(this.formatMoney(tierPriceCents))}</span>
                <span class="pibulk__tier-mid">
                  ${hasBadge ? `<span class="pibulk__tier-badge">${this.escapeHtml(badgeLabel)}</span>` : ''}
                </span>
                <span class="pibulk__tier-threshold">${this.escapeHtml(thresholdLabel)}</span>
              </li>
            `;
          })
          .join('');

        this.volumeTiersElement.innerHTML = `
          <div class="pibulk__tiers-card${hasSelection ? '' : ' pibulk__tiers-card--no-selection'}">
            <ul class="pibulk__tier-list" role="list">
              ${rowsHtml}
            </ul>
          </div>
        `;
      }

      updateMainProductDisplayPrice(variantsOverride, discountStateOverride) {
        const sectionId = String(this.dataset.sectionId || '').trim();
        if (!sectionId) return;

        const priceRoot = document.getElementById(`price-${sectionId}`);
        if (!priceRoot) return;

        const variants = Array.isArray(variantsOverride) ? variantsOverride : this.getFilteredVariants();
        const variantForPrice = this.getVariantForSync(variants) || variants[0] || null;
        if (!variantForPrice) return;

        const discountState = discountStateOverride || this.getVolumeDiscountState();
        const nextPriceCents = this.getDisplayPriceCents(variantForPrice.price, discountState);
        const moneyText = this.formatMoney(nextPriceCents);

        const regularPrimary = priceRoot.querySelector('.f-price__regular .f-price-item--regular');
        const salePrimary = priceRoot.querySelector('.f-price__sale .f-price-item--sale');
        const isOnSale = priceRoot.querySelector('.f-price')?.classList.contains('f-price--on-sale');

        if (isOnSale && salePrimary) {
          salePrimary.textContent = moneyText;
        }
        if (regularPrimary) {
          regularPrimary.textContent = moneyText;
        }

        this.lastAppliedMainPriceCents = nextPriceCents;
        this.lastAppliedMainPriceVariantId = this.getVariantId(variantForPrice);
      }

      queueMainPriceSync(variantsOverride, discountStateOverride) {
        if (this.mainPriceSyncTimer) {
          window.clearTimeout(this.mainPriceSyncTimer);
          this.mainPriceSyncTimer = null;
          if (typeof this.mainPriceSyncOnApplied === 'function') {
            this.mainPriceSyncOnApplied();
            this.mainPriceSyncOnApplied = null;
          }
        }
        this.mainPriceSyncTimer = window.setTimeout(() => {
          this.mainPriceSyncTimer = null;
          this.updateMainProductDisplayPrice(variantsOverride, discountStateOverride);
          if (typeof this.mainPriceSyncOnApplied === 'function') {
            this.mainPriceSyncOnApplied();
            this.mainPriceSyncOnApplied = null;
          }
        }, 80);
      }

      setMainPriceLoading(isLoading) {
        const sectionId = String(this.dataset.sectionId || '').trim();
        if (!sectionId) return;
        const priceRoot = document.getElementById(`price-${sectionId}`);
        if (!priceRoot) return;
        priceRoot.classList.toggle('pibulk-price-loading', Boolean(isLoading));
        if (isLoading) {
          priceRoot.setAttribute('aria-busy', 'true');
        } else {
          priceRoot.removeAttribute('aria-busy');
        }
      }

      formatStockLevelValue(value, fallbackRaw) {
        const numeric = Number.parseFloat(value);
        if (!Number.isFinite(numeric)) {
          const fallbackText = String(fallbackRaw == null ? '' : fallbackRaw).trim();
          return fallbackText || '0';
        }
        const rounded = Math.round(numeric);
        if (Math.abs(numeric - rounded) < 0.0001) return String(rounded);
        return String(Math.round(numeric * 100) / 100);
      }

      updateRapidTooltipAlignment(indicator) {
        if (!indicator) return;
        const tooltip = indicator.querySelector('.pibulk__size-rapid-tooltip');
        if (!tooltip) return;

        indicator.classList.remove('pibulk__size-rapid--align-left', 'pibulk__size-rapid--align-center');

        const margin = 8;
        const triggerRect = indicator.getBoundingClientRect();
        const tooltipWidth = tooltip.offsetWidth || 220;

        const leftEdgeIfRightAligned = triggerRect.right - tooltipWidth;
        if (leftEdgeIfRightAligned >= margin) return;

        const rightEdgeIfLeftAligned = triggerRect.left + tooltipWidth;
        if (rightEdgeIfLeftAligned <= window.innerWidth - margin) {
          indicator.classList.add('pibulk__size-rapid--align-left');
          return;
        }

        indicator.classList.add('pibulk__size-rapid--align-center');
      }

      captureScrollPosition() {
        this.capturedScrollPosition = {
          x: window.scrollX || 0,
          y: window.scrollY || 0,
          capturedAt: Date.now(),
        };
      }

      scheduleScrollRestore() {
        const snapshot = this.capturedScrollPosition;
        if (!snapshot) return;
        if (Date.now() - Number(snapshot.capturedAt || 0) > 2000) return;

        const restore = () => {
          window.scrollTo(snapshot.x, snapshot.y);
        };

        window.requestAnimationFrame(restore);
        window.setTimeout(restore, 60);
        window.setTimeout(() => {
          restore();
          this.capturedScrollPosition = null;
        }, 180);
      }

      async fetchCartJson() {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) {
          throw new Error('Unable to load basket.');
        }
        return response.json();
      }

      buildCartVariantQuantityMap(cart) {
        const map = {};
        if (!cart || !Array.isArray(cart.items)) return map;

        cart.items.forEach((item) => {
          const variantIdRaw = item && item.variant_id != null ? item.variant_id : item && item.id != null ? item.id : '';
          const variantId = String(variantIdRaw || '').trim();
          if (!variantId) return;
          const quantity = Number(item && item.quantity != null ? item.quantity : 0) || 0;
          if (quantity <= 0) return;
          map[variantId] = (Number(map[variantId]) || 0) + quantity;
        });

        return map;
      }

      setCartQuantitiesFromCart(cart) {
        this.cartQuantitiesByVariantId = this.buildCartVariantQuantityMap(cart);
      }

      getCartQuantityForVariant(variantId) {
        if (!variantId) return 0;
        return Number(this.cartQuantitiesByVariantId[String(variantId)] || 0) || 0;
      }

      handleCartUpdated(event) {
        const cart = event && event.detail ? event.detail.cart : null;
        if (!cart) return;
        this.setCartQuantitiesFromCart(cart);
        this.renderSizes();
        this.updateSubmitState();
      }

      async loadInitialCartQuantities() {
        try {
          const cart = await this.fetchCartJson();
          this.setCartQuantitiesFromCart(cart);
          this.renderSizes();
          this.updateSubmitState();
        } catch (error) {
          // best-effort preload only
        }
      }

      normalizeGroup(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      resolveGroupForCartItem(item) {
        const properties = item?.properties || {};
        const fromProperties = this.normalizeGroup(properties._logo_customisation_group || properties['Logo Group'] || '');
        if (fromProperties) return fromProperties;

        const handle = String(item?.handle || '').trim();
        const fromConfig = this.normalizeGroup(window.NewLogoConfig?.products?.byHandle?.[handle] || '');
        if (fromConfig) return fromConfig;

        return '';
      }

      buildCandidateCustomiserUrls(group) {
        const pageConfig = window.NewLogoConfig?.pages || {};
        const mappedUrl = group ? pageConfig.map?.[group] : '';
        const prefix = String(pageConfig.prefix || 'add-logo-');
        const fallback = String(pageConfig.fallback || '/pages/add-logo');

        const candidates = [];
        if (mappedUrl) candidates.push(String(mappedUrl));
        if (group) {
          candidates.push(`/pages/${prefix}${group}`);
          candidates.push(`/pages/${group}`);
        }
        candidates.push(fallback);

        return Array.from(new Set(candidates.filter(Boolean)));
      }

      resolveCustomiserUrl(group) {
        const candidates = this.buildCandidateCustomiserUrls(group);
        return candidates[0] || '/pages/add-logo';
      }

      getRuntimeCustomisationOptionsForItem(item) {
        const runtime = window.NewLogoRuntime || {};
        const byHandle = runtime.customisationOptionsByHandle || {};
        const byProductId = runtime.customisationOptionsByProductId || {};

        const handle = String(item?.handle || item?.product_handle || '').trim();
        const productIdRaw = item?.product_id ?? item?.id ?? '';
        const productId = String(productIdRaw || '').trim();

        let options = [];
        if (handle && Object.prototype.hasOwnProperty.call(byHandle, handle)) {
          options = byHandle[handle];
        } else if (productId && Object.prototype.hasOwnProperty.call(byProductId, productId)) {
          options = byProductId[productId];
        } else {
          const runtimeProduct = runtime.product || {};
          if (handle && String(runtimeProduct.handle || '').trim() === handle) {
            options = runtimeProduct.customisationOptions || [];
          } else if (productId && String(runtimeProduct.id || '').trim() === productId) {
            options = runtimeProduct.customisationOptions || [];
          }
        }

        return Array.isArray(options) ? options : [];
      }

      isItemCustomisable(item) {
        const options = this.getRuntimeCustomisationOptionsForItem(item);
        if (options.length > 0) return true;
        const group = this.resolveGroupForCartItem(item);
        return Boolean(group);
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      getCartItemImageUrl(item) {
        const source = item?.featured_image?.url || item?.image || '';
        const normalized = String(source || '').trim();
        if (!normalized) return '';
        if (normalized.indexOf('//') === 0) return `${window.location.protocol}${normalized}`;
        return normalized;
      }

      collectAddedEntries(beforeCart, afterCart, requestedItems) {
        if (!Array.isArray(afterCart?.items) || !Array.isArray(requestedItems) || !requestedItems.length) return [];

        const requestedByVariantId = {};
        requestedItems.forEach((item) => {
          const variantId = String(item?.id || '');
          if (!variantId) return;
          requestedByVariantId[variantId] = (Number(requestedByVariantId[variantId]) || 0) + (Number(item?.quantity) || 0);
        });

        const hasBeforeSnapshot = Array.isArray(beforeCart?.items);
        const beforeQuantityByKey = {};
        if (hasBeforeSnapshot) {
          beforeCart.items.forEach((item) => {
            const key = String(item?.key || '');
            if (!key) return;
            beforeQuantityByKey[key] = Number(item?.quantity) || 0;
          });
        }

        const entries = [];
        afterCart.items.forEach((item, index) => {
          const variantId = String(item?.variant_id ?? item?.id ?? '');
          if (!Object.prototype.hasOwnProperty.call(requestedByVariantId, variantId)) return;

          const key = String(item?.key || '');
          const currentQty = Number(item?.quantity) || 0;
          let delta = 0;

          if (hasBeforeSnapshot) {
            delta = currentQty - (Number(beforeQuantityByKey[key]) || 0);
          } else {
            const remainingRequested = Number(requestedByVariantId[variantId]) || 0;
            delta = Math.min(currentQty, remainingRequested > 0 ? remainingRequested : currentQty);
          }

          if (delta <= 0) return;

          requestedByVariantId[variantId] = Math.max(0, (Number(requestedByVariantId[variantId]) || 0) - delta);
          entries.push({
            key,
            line: index + 1,
            quantity: delta,
            item,
          });
        });

        return entries;
      }

      buildAddedSummary(entries) {
        const firstEntry = entries[0] || {};
        const firstItem = firstEntry.item || {};
        const variantRows = [];
        let totalQty = 0;
        let totalCents = 0;

        entries.forEach((entry) => {
          const item = entry.item || {};
          const quantity = Number(entry.quantity) || 0;
          if (quantity <= 0) return;

          const variantTitle = String(item.variant_title || '').trim();
          const rowLabel =
            variantTitle && variantTitle.toLowerCase() !== 'default title'
              ? variantTitle
              : String(item.title || item.product_title || 'Variant').trim();

          variantRows.push({
            label: rowLabel,
            quantity,
          });

          const unitCents = Number(item.final_price ?? item.price ?? item.original_price ?? 0) || 0;
          totalQty += quantity;
          totalCents += unitCents * quantity;
        });

        const perItemCents = totalQty > 0 ? Math.round(totalCents / totalQty) : 0;

        return {
          title: String(firstItem.product_title || firstItem.title || 'Added items'),
          imageUrl: this.getCartItemImageUrl(firstItem),
          totalQty,
          totalCents,
          perItemCents,
          variantRows,
        };
      }

      buildCustomiserUrlForAddedEntries(entries, cart) {
        if (!Array.isArray(entries) || !entries.length || !Array.isArray(cart?.items)) return '';

        const targetKeys = entries
          .map((entry) => String(entry?.key || '').trim())
          .filter(Boolean);
        if (!targetKeys.length) return '';

        const primaryEntry = entries[0];
        const primaryKey = String(primaryEntry?.key || targetKeys[0] || '');
        if (!primaryKey) return '';

        const primaryItem = primaryEntry?.item || cart.items.find((item) => item.key === primaryKey) || {};
        if (!this.isItemCustomisable(primaryItem)) return '';

        const group = this.resolveGroupForCartItem(primaryItem);
        const baseUrl = this.resolveCustomiserUrl(group);
        const line =
          Number(primaryEntry?.line) > 0
            ? Number(primaryEntry.line)
            : cart.items.findIndex((item) => item.key === primaryKey) + 1;

        const url = new URL(baseUrl, window.location.origin);
        if (line > 0) url.searchParams.set('line', String(line));
        url.searchParams.set('key', primaryKey);
        if (group) url.searchParams.set('customisation_group', group);
        url.searchParams.set('target_keys', targetKeys.join(','));
        return url.toString();
      }

      ensureAddedModal() {
        if (this.addedModal && document.body.contains(this.addedModal)) return this.addedModal;

        let modal = document.getElementById(this.addedModalId);
        if (!modal) {
          modal = document.createElement('div');
          modal.id = this.addedModalId;
          modal.className = 'pibulk-added-modal';
          modal.hidden = true;
          modal.innerHTML = `
            <div class="pibulk-added-modal__overlay" data-pibulk-modal-close></div>
            <div class="pibulk-added-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="pibulk-added-modal-title"></div>
          `;
          document.body.appendChild(modal);
        }

        if (!modal.__pibulkBound) {
          modal.addEventListener('click', (event) => {
            const closeTarget = event.target.closest('[data-pibulk-modal-close], [data-pibulk-continue]');
            if (closeTarget) {
              event.preventDefault();
              this.closeAddedModal();
              return;
            }

            const viewBasketTarget = event.target.closest('[data-pibulk-view-basket]');
            if (viewBasketTarget) {
              event.preventDefault();
              this.closeAddedModal();
              this.openBasketFromAddedModal();
              return;
            }

            const addLogoTarget = event.target.closest('[data-pibulk-add-logo]');
            if (addLogoTarget) {
              this.closeAddedModal();
            }
          });
          modal.__pibulkBound = true;
        }

        this.addedModal = modal;
        return modal;
      }

      isAddedModalOpen() {
        return Boolean(this.addedModal && !this.addedModal.hidden);
      }

      handleAddedModalKeydown(event) {
        if (event.key !== 'Escape') return;
        if (!this.isAddedModalOpen()) return;
        this.closeAddedModal();
      }

      closeAddedModal() {
        if (!this.addedModal) return;
        this.addedModal.hidden = true;
        document.body.classList.remove('pibulk-modal-open');
        document.removeEventListener('keydown', this.handleAddedModalKeydownBound);
      }

      openBasketFromAddedModal() {
        if (document.querySelector('cart-drawer')) {
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { open: true },
            })
          );
          return;
        }

        const cartUrl = window.FoxTheme?.routes?.cart_url || '/cart';
        window.location.href = cartUrl;
      }

      showAddedModal(entries, cart) {
        if (!Array.isArray(entries) || !entries.length || !cart) return;

        const modal = this.ensureAddedModal();
        const dialog = modal?.querySelector('.pibulk-added-modal__dialog');
        if (!dialog) return;

        const summary = this.buildAddedSummary(entries);
        const customiserUrl = this.buildCustomiserUrlForAddedEntries(entries, cart);
        if (!customiserUrl) {
          this.openBasketFromAddedModal();
          return;
        }
        const actionsClass = customiserUrl
          ? 'pibulk-added-modal__actions'
          : 'pibulk-added-modal__actions pibulk-added-modal__actions--single';
        const logoActionMarkup = customiserUrl
          ? `<a class="btn btn--primary pibulk-added-modal__button pibulk-added-modal__button--logo" href="${this.escapeHtml(
              customiserUrl
            )}" data-pibulk-add-logo data-new-logo-link>Add your logo now</a>`
          : '';

        const variantsMarkup = summary.variantRows
          .map(
            (row) =>
              `<li><span>${this.escapeHtml(row.label)}</span><strong>Qty: ${Number(row.quantity) || 0}</strong></li>`
          )
          .join('');

        dialog.innerHTML = `
          <div class="pibulk-added-modal__card">
            <div class="pibulk-added-modal__head">
              <p class="pibulk-added-modal__title" id="pibulk-added-modal-title">
                <span class="pibulk-added-modal__title-icon" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="pibulk-added-modal__title-icon-svg bi bi-check-circle"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                    <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
                  </svg>
                </span>
                Added to basket
              </p>
              <button type="button" class="pibulk-added-modal__close" data-pibulk-modal-close aria-label="Close">×</button>
            </div>
            <div class="pibulk-added-modal__product">
              ${
                summary.imageUrl
                  ? `<img class="pibulk-added-modal__image" src="${this.escapeHtml(summary.imageUrl)}" alt="${this.escapeHtml(
                      summary.title
                    )}" loading="lazy" width="96" height="96">`
                  : ''
              }
              <div class="pibulk-added-modal__product-copy">
                <h3 class="pibulk-added-modal__product-title">${this.escapeHtml(summary.title)}</h3>
                <ul class="pibulk-added-modal__variants">${variantsMarkup}</ul>
              </div>
            </div>
            <div class="pibulk-added-modal__totals">
              <p class="pibulk-added-modal__items">${summary.totalQty} item${summary.totalQty === 1 ? '' : 's'}</p>
              <p class="pibulk-added-modal__total">Total: <strong>${this.formatMoney(summary.totalCents)}</strong></p>
            </div>
            <div class="${actionsClass}">
              ${logoActionMarkup}
              <button type="button" class="btn btn--secondary pibulk-added-modal__button" data-pibulk-view-basket>View basket</button>
            </div>
            <button type="button" class="btn--link pibulk-added-modal__continue" data-pibulk-continue>Continue shopping</button>
          </div>
        `;

        modal.hidden = false;
        document.body.classList.add('pibulk-modal-open');
        document.addEventListener('keydown', this.handleAddedModalKeydownBound);
      }

      getAvailabilityText(variant, cartQuantityOverride) {
        const stock = this.getVariantStockRecord(variant) || variant || {};
        if (!stock.available) return '0 available';

        const quantity = Number(stock.inventory_quantity);
        const checkAgainstInventory = stock.inventory_management === 'shopify' && stock.inventory_policy !== 'continue';
        const variantId = this.getVariantId(variant);
        const cartQuantity =
          Number.isFinite(Number(cartQuantityOverride))
            ? Number(cartQuantityOverride)
            : this.getCartQuantityForVariant(variantId);
        const formatQuantity = (value) => (value > 100 ? '100+' : String(Math.max(0, value)));

        if (checkAgainstInventory && Number.isFinite(quantity)) {
          const remaining = Math.max(0, quantity - Math.max(0, cartQuantity));
          return `${formatQuantity(remaining)} available`;
        }

        if (Number.isFinite(quantity) && quantity > 0) {
          return `${formatQuantity(quantity)} available`;
        }

        const maxRule =
          Number(stock.quantity_rule_max) ||
          Number(stock && stock.quantity_rule && stock.quantity_rule.max);
        if (Number.isFinite(maxRule) && maxRule > 0) {
          return `${maxRule} available`;
        }

        return '100+ available';
      }

      getVariantDisplayTitle(variant) {
        if (!variant || typeof variant !== 'object') return '';
        const optionValues = [variant.option1, variant.option2, variant.option3]
          .map((value) => this.normalizeOptionText(value))
          .filter(Boolean);
        if (optionValues.length) return optionValues.join(' / ');
        return this.normalizeOptionText(variant.title) || 'Variant';
      }

      initBackInStockModal() {
        if (this.backInStockModal) return;

        const modal = document.createElement('div');
        modal.className = 'pibulk-bis-modal';
        modal.hidden = true;
        modal.innerHTML = `
          <div class="pibulk-bis-modal__overlay" data-pibulk-bis-close></div>
          <div class="pibulk-bis-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="PIBulkBisTitle">
            <button type="button" class="pibulk-bis-modal__close" data-pibulk-bis-close aria-label="Close">×</button>
            <h3 id="PIBulkBisTitle" class="pibulk-bis-modal__title">Notify me</h3>
            <p class="pibulk-bis-modal__variant" data-pibulk-bis-variant></p>
            <form class="pibulk-bis-modal__form" data-pibulk-bis-form novalidate>
              <input type="hidden" name="variantId" value="">
              <label class="pibulk-bis-modal__field">
                <span>Email</span>
                <input type="email" name="email" required autocomplete="email">
              </label>
              <button type="submit" class="pibulk-bis-modal__submit">Notify me</button>
              <p class="pibulk-bis-modal__error" data-pibulk-bis-error hidden></p>
            </form>
            <div class="pibulk-bis-modal__success" data-pibulk-bis-success hidden>
              <span class="pibulk-bis-modal__success-icon" aria-hidden="true">
                <svg viewBox="0 0 52 52" focusable="false">
                  <circle class="pibulk-bis-modal__success-circle" cx="26" cy="26" r="24"></circle>
                  <path class="pibulk-bis-modal__success-check" d="M14 27.5l7.5 7.5L38 18.5"></path>
                </svg>
              </span>
              <p class="pibulk-bis-modal__success-text">
                Thanks, we’ll email you when this variant is back in stock.
              </p>
            </div>
          </div>
        `;

        const closeElements = modal.querySelectorAll('[data-pibulk-bis-close]');
        closeElements.forEach((node) => {
          node.addEventListener('click', () => this.closeBackInStockModal());
        });

        const form = modal.querySelector('[data-pibulk-bis-form]');
        if (form) {
          form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.submitBackInStockRequest(form);
          });
        }

        document.body.appendChild(modal);
        this.backInStockModal = modal;
      }

      destroyBackInStockModal() {
        if (!this.backInStockModal) return;
        document.removeEventListener('keydown', this.handleBackInStockKeydownBound);
        this.backInStockModal.remove();
        this.backInStockModal = null;
      }

      openBackInStockModal(variant) {
        if (!this.backInStockModal || !variant) return;
        const form = this.backInStockModal.querySelector('[data-pibulk-bis-form]');
        const variantElement = this.backInStockModal.querySelector('[data-pibulk-bis-variant]');
        const success = this.backInStockModal.querySelector('[data-pibulk-bis-success]');
        const error = this.backInStockModal.querySelector('[data-pibulk-bis-error]');
        if (!form || !variantElement || !success || !error) return;

        const variantId = this.getVariantId(variant);
        const variantTitle = this.getVariantDisplayTitle(variant);
        variantElement.textContent = `${this.productTitle} — ${variantTitle}`;

        const variantInput = form.querySelector('input[name="variantId"]');
        if (variantInput) variantInput.value = variantId;

        form.reset();
        if (variantInput) variantInput.value = variantId;
        form.hidden = false;
        success.hidden = true;
        error.hidden = true;
        error.textContent = '';
        this.backInStockSubmitting = false;

        this.backInStockModal.hidden = false;
        this.backInStockModal.classList.remove('is-success');
        document.body.classList.add('pibulk-modal-open');
        document.addEventListener('keydown', this.handleBackInStockKeydownBound);
      }

      closeBackInStockModal() {
        if (!this.backInStockModal) return;
        this.backInStockModal.hidden = true;
        this.backInStockSubmitting = false;
        document.removeEventListener('keydown', this.handleBackInStockKeydownBound);

        if (!this.addedModal || this.addedModal.hidden) {
          document.body.classList.remove('pibulk-modal-open');
        }
      }

      handleBackInStockKeydown(event) {
        if (!event || event.key !== 'Escape') return;
        this.closeBackInStockModal();
      }

      async submitBackInStockRequest(form) {
        if (!form || this.backInStockSubmitting) return;
        const success = this.backInStockModal && this.backInStockModal.querySelector('[data-pibulk-bis-success]');
        const error = this.backInStockModal && this.backInStockModal.querySelector('[data-pibulk-bis-error]');
        const submitButton = form.querySelector('.pibulk-bis-modal__submit');
        const variantIdInput = form.querySelector('input[name="variantId"]');
        const emailInput = form.querySelector('input[name="email"]');

        const variantId = String(variantIdInput ? variantIdInput.value : '').trim();
        const email = String(emailInput ? emailInput.value : '').trim();
        const variant = this.getVariantById(variantId);
        const variantTitle = this.getVariantDisplayTitle(variant);

        if (!variantId || !email) {
          if (error) {
            error.hidden = false;
            error.textContent = 'Please enter your email address.';
          }
          return;
        }

        this.backInStockSubmitting = true;
        if (submitButton) submitButton.disabled = true;
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }

        let submitted = false;

        try {
          if (window.Brevo && typeof window.Brevo.push === 'function') {
            window.Brevo.push([
              'track',
              'back_in_stock_request',
              {
                variant_id: variantId,
                email,
                product_title: this.productTitle,
                variant_title: variantTitle,
              },
            ]);
            submitted = true;
          } else if (this.brevoApiKey) {
            const payload = {
              productId: `shopify_variant_${variantId}`,
              email,
              properties: {
                VARIANT_TITLE: variantTitle,
                PRODUCT_TITLE: this.productTitle,
              },
            };
            const response = await fetch(this.brevoApiEndpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'api-key': this.brevoApiKey,
              },
              body: JSON.stringify(payload),
            });
            submitted = response.ok;
          }
        } catch (submitError) {
          submitted = false;
        }

        if (submitted) {
          if (success) success.hidden = false;
          form.hidden = true;
          if (this.backInStockModal) this.backInStockModal.classList.add('is-success');
        } else if (error) {
          error.hidden = false;
          error.textContent = 'Could not submit right now. Please try again.';
          if (this.backInStockModal) this.backInStockModal.classList.remove('is-success');
        }

        if (submitButton) submitButton.disabled = false;
        this.backInStockSubmitting = false;
      }

      getVariantSelectsElement() {
        if (this.variantSelectsElement && document.body.contains(this.variantSelectsElement)) {
          return this.variantSelectsElement;
        }
        const productInfo = this.getMainProductInfoElement();
        if (productInfo) {
          this.variantSelectsElement = productInfo.querySelector('variant-selects');
          if (this.variantSelectsElement) return this.variantSelectsElement;
        }

        const sectionScoped = document.getElementById(`variant-selects-${this.dataset.sectionId || ''}`);
        if (sectionScoped) {
          this.variantSelectsElement = sectionScoped;
          return this.variantSelectsElement;
        }

        const productScoped = this.findVariantSelectsByProductId();
        this.variantSelectsElement = productScoped;
        return this.variantSelectsElement;
      }

      getProductIdKey() {
        return String(this.dataset.productId || '').trim();
      }

      findProductInfoByProductId() {
        const productId = this.getProductIdKey();
        if (!productId) return null;

        const matches = Array.from(document.querySelectorAll(`product-info[data-product-id="${productId}"]`));
        if (!matches.length) return null;

        const mainMatch = matches.find(
          (element) => element.id && element.id.indexOf('MainProduct-') === 0 && element.querySelector('variant-selects')
        );
        if (mainMatch) return mainMatch;

        const withVariantSelects = matches.find((element) => element.querySelector('variant-selects'));
        return withVariantSelects || matches[0];
      }

      findVariantSelectsByProductId() {
        const productInfo = this.findProductInfoByProductId();
        return productInfo ? productInfo.querySelector('variant-selects') : null;
      }

      getMainProductInfoElement() {
        if (this.mainProductInfoElement && document.body.contains(this.mainProductInfoElement)) {
          return this.mainProductInfoElement;
        }

        const closestProductInfo = this.closest('product-info');
        if (closestProductInfo) {
          this.mainProductInfoElement = closestProductInfo;
          return this.mainProductInfoElement;
        }

        const sectionId = this.dataset.sectionId || '';
        const byId = document.getElementById(`MainProduct-${sectionId}`);
        if (byId) {
          this.mainProductInfoElement = byId;
          return this.mainProductInfoElement;
        }

        const byProductId = this.findProductInfoByProductId();
        if (byProductId) {
          this.mainProductInfoElement = byProductId;
          return this.mainProductInfoElement;
        }

        const variantSelects = document.getElementById(`variant-selects-${sectionId}`);
        this.mainProductInfoElement = variantSelects ? variantSelects.closest('product-info') : null;
        return this.mainProductInfoElement;
      }

      getOptionContainers(variantSelects) {
        if (!variantSelects) return [];
        return Array.from(variantSelects.children).filter(
          (element) => element.matches && element.matches('.product-form__input')
        );
      }

      setControlToOptionValue(container, optionValue) {
        if (!container || !optionValue) return null;
        const targetKey = this.getOptionKey(optionValue);
        if (!targetKey) return null;

        const select = container.querySelector('select');
        if (select) {
          const options = Array.from(select.options || []);
          const matched = options.find((option) => this.getOptionKey(option.value) === targetKey);
          if (!matched) return null;

          const wasSelected = matched.selected && select.value === matched.value;
          options.forEach((option) => option.removeAttribute('selected'));
          matched.selected = true;
          matched.setAttribute('selected', 'selected');
          select.value = matched.value;

          return wasSelected ? null : select;
        }

        const radios = Array.from(container.querySelectorAll('input[type="radio"]'));
        const matchedRadio = radios.find((input) => this.getOptionKey(input.value) === targetKey);
        if (!matchedRadio) return null;
        if (matchedRadio.checked) return null;
        matchedRadio.checked = true;
        return matchedRadio;
      }

      findControlForOptionValue(container, optionValue) {
        if (!container || !optionValue) return null;
        const targetKey = this.getOptionKey(optionValue);
        if (!targetKey) return null;

        const select = container.querySelector('select');
        if (select) {
          const options = Array.from(select.options || []);
          return options.find((option) => this.getOptionKey(option.value) === targetKey) ? select : null;
        }

        const radios = Array.from(container.querySelectorAll('input[type="radio"]'));
        return radios.find((input) => this.getOptionKey(input.value) === targetKey) || null;
      }

      updateMainProductFragment(productInfo, parsedHtml, baseSectionId, id, shouldHide) {
        const source = parsedHtml.getElementById(`${id}-${baseSectionId}`);
        const destination = productInfo.querySelector(`#${id}-${productInfo.dataset.section}`);
        if (!source || !destination) return;

        destination.innerHTML = source.innerHTML;
        if (typeof shouldHide === 'function') {
          destination.classList.toggle('hidden', shouldHide(source));
        }
      }

      async hardSyncMainProductVariant(variant) {
        const variantId = this.getVariantId(variant);
        if (!variantId) return;
        const mediaVariant = this.getMediaFallbackVariant(variant);

        const productInfo = this.getMainProductInfoElement();
        if (!productInfo) return;

        const baseSectionId = productInfo.sectionId || productInfo.dataset.section;
        const productUrl = productInfo.dataset.url;
        if (!baseSectionId || !productUrl) return;

        if (this.variantSyncAbortController) {
          this.variantSyncAbortController.abort();
        }
        const abortController = new AbortController();
        this.variantSyncAbortController = abortController;
        let loadingReleased = false;
        const discountStateNow = this.getVolumeDiscountState();
        const baseVariantPriceCents = Number(variant && variant.price != null ? variant.price : 0) || 0;
        const projectedNextPriceCents = this.getDisplayPriceCents(baseVariantPriceCents, discountStateNow);
        const shouldShowPriceSkeleton = Number(projectedNextPriceCents) !== Number(baseVariantPriceCents);
        const releaseLoading = () => {
          if (loadingReleased) return;
          loadingReleased = true;
          if (shouldShowPriceSkeleton) this.setMainPriceLoading(false);
        };
        if (shouldShowPriceSkeleton) this.setMainPriceLoading(true);

        try {
          const response = await fetch(`${productUrl}?variant=${variantId}&section_id=${baseSectionId}`, {
            signal: abortController.signal,
          });
          if (!response.ok) {
            releaseLoading();
            return;
          }

          const responseText = await response.text();
          if (abortController !== this.variantSyncAbortController) {
            releaseLoading();
            return;
          }
          const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');

          if (
            typeof productInfo.updateOptionValues === 'function' &&
            productInfo.querySelector('variant-selects') &&
            parsedHtml.querySelector('variant-selects')
          ) {
            productInfo.updateOptionValues(parsedHtml);
          }
          if (typeof productInfo.updateURL === 'function') {
            productInfo.updateURL(productUrl, variant.id);
          }
          if (typeof productInfo.updateShareUrl === 'function') {
            productInfo.updateShareUrl(variant.id);
          }
          if (typeof productInfo.updateVariantInputs === 'function') {
            productInfo.updateVariantInputs(variant.id);
          }
          if (typeof productInfo.updateMedia === 'function') {
            productInfo.updateMedia(mediaVariant || variant);
          }
          this.applyVariantMediaRangeFilter(productInfo, mediaVariant || variant);

          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'price');
          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'Sku', (source) =>
            source.classList.contains('hidden')
          );
          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'Barcode', (source) =>
            source.classList.contains('hidden')
          );
          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'Inventory', (source) =>
            source.innerText.trim() === ''
          );
          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'Badges', (source) =>
            source.classList.contains('hidden')
          );
          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'PricePerItem', (source) =>
            source.classList.contains('hidden')
          );
          this.updateMainProductFragment(productInfo, parsedHtml, baseSectionId, 'Volume');

          if (typeof productInfo.updateQuantityRules === 'function') {
            productInfo.updateQuantityRules(baseSectionId, productInfo.productId, parsedHtml);
            productInfo.querySelector(`#QuantityRules-${productInfo.dataset.section}`)?.classList.remove('hidden');
            productInfo.querySelector(`#VolumeNote-${productInfo.dataset.section}`)?.classList.remove('hidden');
          }

          const addButtonUpdated = parsedHtml.getElementById(`ProductSubmitButton-${baseSectionId}`);
          if (typeof productInfo.toggleAddButton === 'function') {
            productInfo.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              window.FoxTheme?.variantStrings?.soldOut
            );
          }

          productInfo.currentVariant = variant;
          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: { variant },
            })
          );

          if (
            window.FoxTheme &&
            window.FoxTheme.pubsub &&
            typeof window.FoxTheme.pubsub.publish === 'function' &&
            window.FoxTheme.pubsub.PUB_SUB_EVENTS &&
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange
          ) {
            window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: baseSectionId,
                html: parsedHtml,
                variant,
              },
            });
          }
          this.queueMainPriceSync(this.getFilteredVariants(), this.getVolumeDiscountState());
          this.mainPriceSyncOnApplied = releaseLoading;
          this.scheduleScrollRestore();
        } catch (error) {
          releaseLoading();
          if (error.name === 'AbortError') return;
          this.scheduleScrollRestore();
        }
      }

      syncMainProductVariant(variant) {
        if (!variant) return;
        const variantSelects = this.getVariantSelectsElement();
        const optionValues = [variant.option1, variant.option2, variant.option3];
        const controls = variantSelects ? Array.from(variantSelects.querySelectorAll('select, fieldset')) : [];

        controls.forEach((element, index) => {
          const variantOptionValue = optionValues[index];
          if (!variantOptionValue) return;

          if (element.tagName === 'SELECT') {
            const options = Array.from(element.options || []);
            options.forEach((option) => option.removeAttribute('selected'));

            element.value = variantOptionValue;
            const selectedOption = options.find(
              (option) => this.getOptionKey(option.value) === this.getOptionKey(variantOptionValue)
            );
            if (selectedOption) {
              selectedOption.selected = true;
              selectedOption.setAttribute('selected', 'selected');
              element.value = selectedOption.value;
            }
            return;
          }

          if (element.tagName === 'FIELDSET') {
            Array.from(element.querySelectorAll('input[type="radio"]')).forEach((radio) => {
              const isMatch = this.getOptionKey(radio.value) === this.getOptionKey(variantOptionValue);
              radio.checked = isMatch;
            });
          }
        });

        this.hardSyncMainProductVariant(variant);
      }

      render() {
        this.renderOptionGroups();
        this.renderSizes();
        this.updateSubmitState();
      }

      renderOptionGroups() {
        if (!this.optionGroupsElement) return;

        this.teardownSwatchOverflowHints();
        this.optionGroupsElement.innerHTML = '';
        if (!this.optionDefinitions.length) return;

        this.optionDefinitions.forEach((definition) => {
          const visibleValues = this.getRenderableDefinitionValues(definition);
          if (!visibleValues.length) return;

          const group = document.createElement('div');
          group.className = 'pibulk__group pibulk__option-group';

          const heading = document.createElement('h3');
          heading.className = 'pibulk__heading';
          heading.textContent = definition.name;
          group.appendChild(heading);

          const optionsWrap = document.createElement('div');
          let swatchContainer = null;
          const groupName = `${this.groupPrefix}-option-${definition.index}`;

          if (definition.type === 'color') {
            swatchContainer = document.createElement('div');
            swatchContainer.className = 'pibulk__swatch-row-wrap';
            optionsWrap.className =
              'pibulk__swatches product-form__input product-form__input--color product-form__input--button-standard js flex flex-wrap gap-4';
            optionsWrap.setAttribute('role', 'radiogroup');
            optionsWrap.setAttribute('aria-label', definition.name);
            optionsWrap.style.setProperty('--swatch-size', '4.6rem');
          } else {
            optionsWrap.className =
              'pibulk__option-buttons product-form__input product-form__input--button product-form__input--button-standard js flex flex-wrap gap-2';
            optionsWrap.setAttribute('role', 'radiogroup');
            optionsWrap.setAttribute('aria-label', definition.name);
          }

          visibleValues.forEach((value, index) => {
            const input = document.createElement('input');
            const inputId = `${groupName}-${index}`;
            const isChecked = this.getOptionKey(this.selectedOptions[definition.index]) === this.getOptionKey(value.label);

            input.type = 'radio';
            input.id = inputId;
            input.name = groupName;
            input.value = value.label;
            input.checked = isChecked;
            if (definition.type === 'color' && value.soldOut) input.classList.add('disabled');
            input.addEventListener('change', () => {
              if (!input.checked) return;
              this.selectedOptions[definition.index] = value.label;
              const normalizedChanged = this.normalizeSelectedOptions(definition.index);
              this.retainVisibleQuantities();
              const variants = this.getFilteredVariants();
              if (normalizedChanged) this.renderOptionGroups();
              this.renderSizes(variants);
              this.updateSubmitState();
              this.syncMainVariantFromSelection(variants);
            });

            const label = document.createElement('label');
            label.setAttribute('for', inputId);

            if (definition.type === 'color') {
              label.className = 'block relative';
              label.setAttribute('data-swatch-type', value.image ? 'variant_image' : 'color_swatch');
              label.setAttribute('data-tooltip', '');
              label.setAttribute('title', value.label);
              label.setAttribute('aria-label', value.label);

              const tooltip = document.createElement('span');
              tooltip.className = 'tooltip pointer-events-none';
              tooltip.textContent = value.label;

              const swatchChip = document.createElement('span');
              swatchChip.className = 'swatch-color block';
              swatchChip.style.setProperty('--swatch-background', this.getSwatchBackground(value));

              label.append(tooltip, swatchChip);
            } else {
              label.textContent = value.label;
            }

            optionsWrap.append(input, label);
          });

          if (swatchContainer) {
            swatchContainer.append(optionsWrap);
            group.appendChild(swatchContainer);
            this.setupSwatchOverflowHint(swatchContainer, optionsWrap);
          } else {
            group.appendChild(optionsWrap);
          }
          this.optionGroupsElement.appendChild(group);
        });

      }

      teardownSwatchOverflowHints() {
        if (!Array.isArray(this.swatchHintCleanup) || !this.swatchHintCleanup.length) return;
        this.swatchHintCleanup.forEach((cleanup) => {
          if (typeof cleanup === 'function') cleanup();
        });
        this.swatchHintCleanup = [];
      }

      setupSwatchOverflowHint(container, swatchRow) {
        if (!container || !swatchRow) return;
        let hasSwiped = false;

        const update = () => {
          const maxScroll = swatchRow.scrollWidth - swatchRow.clientWidth;
          const hasOverflow = maxScroll > 4;
          const atEnd = !hasOverflow || swatchRow.scrollLeft >= maxScroll - 4;
          hasSwiped = hasSwiped || swatchRow.scrollLeft > 6;

          container.classList.toggle('has-overflow', hasOverflow);
          container.classList.toggle('is-end', atEnd);
          container.classList.toggle('is-swiped', hasSwiped);
        };

        const handleScroll = () => update();
        const handleResize = () => update();

        swatchRow.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);
        window.requestAnimationFrame(update);

        this.swatchHintCleanup.push(() => {
          swatchRow.removeEventListener('scroll', handleScroll);
          window.removeEventListener('resize', handleResize);
        });
      }

      getMaxAllowed(variant, cartQuantityOverride) {
        const stock = this.getVariantStockRecord(variant) || variant || {};
        if (!stock.available) return 0;

        const managesStock = stock.inventory_management === 'shopify';
        const canBackorder = stock.inventory_policy === 'continue';
        const quantity = Number(stock.inventory_quantity || 0);
        const variantId = this.getVariantId(variant);
        const cartQuantity =
          Number.isFinite(Number(cartQuantityOverride))
            ? Number(cartQuantityOverride)
            : this.getCartQuantityForVariant(variantId);

        if (!managesStock || canBackorder) return 999;
        if (!Number.isFinite(quantity)) return 0;
        return Math.max(0, quantity - Math.max(0, cartQuantity));
      }

      setVariantQuantity(variantId, nextQuantity, maxAllowed) {
        const parsed = Number.parseInt(nextQuantity, 10);
        const safeValue = Number.isFinite(parsed) ? parsed : 0;
        const upperBound = Number.isFinite(maxAllowed) ? maxAllowed : 999;
        const clamped = Math.max(0, Math.min(safeValue, upperBound));

        if (clamped > 0) {
          this.quantities[variantId] = clamped;
        } else {
          delete this.quantities[variantId];
        }

        return clamped;
      }

      scheduleVariantSync(variant) {
        const variantId = this.getVariantId(variant);
        if (!variantId) return;

        this.pendingVariantSyncId = variantId;
        if (this.pendingVariantSyncTimer) {
          window.clearTimeout(this.pendingVariantSyncTimer);
          this.pendingVariantSyncTimer = null;
        }

        this.pendingVariantSyncTimer = window.setTimeout(() => {
          const latestVariant = this.getVariantById(this.pendingVariantSyncId) || variant;
          this.pendingVariantSyncTimer = null;
          this.pendingVariantSyncId = '';
          if (!latestVariant) return;
          this.syncMainProductVariant(latestVariant);
        }, 140);
      }

      renderSizes(variantsOverride) {
        if (!this.sizesElement) return;

        const variants = Array.isArray(variantsOverride) ? variantsOverride : this.getFilteredVariants();
        const volumeDiscountState = this.getVolumeDiscountState();
        const visibleVariants = this.showOutOfStockVariants
          ? variants
          : variants.filter((variant) => this.isVariantSellable(variant));
        this.sizesElement.innerHTML = '';

        visibleVariants.forEach((variant) => {
          const variantId = this.getVariantId(variant);
          const stockRecord = this.getVariantStockRecord(variant) || null;
          const quantity = Number(this.quantities[variantId] || 0);
          const cartQuantity = this.getCartQuantityForVariant(variantId);
          const available = Boolean(variant.available);
          const sizeLabel = this.getOptionValue(variant, this.sizeIndex) || variant.title;
          const maxAllowed = this.getMaxAllowed(variant, cartQuantity);
          const canIncrease = available && quantity < maxAllowed;
          const isOutOfStock = !available;
          const blockedByCartAllocation = available && maxAllowed <= 0;

          const card = document.createElement('article');
          card.className = `pibulk__size${quantity > 0 ? ' is-selected' : ''}${available ? '' : ' is-disabled'}`;

          const sizeName = document.createElement('p');
          sizeName.className = 'pibulk__size-name';
          const sizeNameText = document.createElement('span');
          sizeNameText.className = 'pibulk__size-name-text';
          sizeNameText.textContent = sizeLabel;
          sizeName.appendChild(sizeNameText);

          const rapidInventoryRaw = stockRecord && stockRecord.rapid_inventory_level != null
            ? stockRecord.rapid_inventory_level
            : 0;
          const rapidInventoryValue = Number.parseFloat(rapidInventoryRaw);
          const combinedVariantQuantity = cartQuantity + quantity;
          const isQuickDispatchWithinLimit =
            Number.isFinite(rapidInventoryValue) &&
            rapidInventoryValue > 0 &&
            combinedVariantQuantity <= rapidInventoryValue;

          if (isQuickDispatchWithinLimit) {
            const storeStockText = this.formatStockLevelValue(rapidInventoryValue, rapidInventoryRaw);
            const quickDispatchText = `${storeStockText} ready to ship`;

            const rapidIndicator = document.createElement('span');
            rapidIndicator.className = 'pibulk__size-rapid';
            rapidIndicator.setAttribute('aria-label', quickDispatchText);
            rapidIndicator.setAttribute('tabindex', '0');
            rapidIndicator.innerHTML = `
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                stroke="currentColor"
                stroke-width="0.65"
                stroke-linejoin="round"
                class="bi bi-lightning-charge"
                aria-hidden="true"
              >
                <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09zM4.157 8.5H7a.5.5 0 0 1 .478.647L6.11 13.59l5.732-6.09H9a.5.5 0 0 1-.478-.647L9.89 2.41z"/>
              </svg>
              <span class="pibulk__size-rapid-tooltip" role="tooltip">
                <span class="pibulk__size-rapid-title">Quick despatch</span>
                <span class="pibulk__size-rapid-row">
                  <span class="pibulk__size-rapid-value">${this.escapeHtml(storeStockText)}</span>
                  <span class="pibulk__size-rapid-label">ready to ship</span>
                </span>
              </span>
            `;
            const handleTooltipAlign = () => this.updateRapidTooltipAlignment(rapidIndicator);
            rapidIndicator.addEventListener('mouseenter', handleTooltipAlign);
            rapidIndicator.addEventListener('focus', handleTooltipAlign);
            sizeName.appendChild(rapidIndicator);
          }

          const qtyWrap = document.createElement('div');
          qtyWrap.className = 'pibulk__qty';
          qtyWrap.setAttribute('role', 'group');
          qtyWrap.setAttribute('aria-label', `Quantity for ${sizeLabel}`);

          const decreaseButton = document.createElement('button');
          decreaseButton.type = 'button';
          decreaseButton.className = 'pibulk__qty-btn';
          decreaseButton.textContent = '−';
          decreaseButton.disabled = quantity <= 0;
          decreaseButton.addEventListener('click', () => {
            this.captureScrollPosition();
            const current = Number(this.quantities[variantId] || 0);
            this.setVariantQuantity(variantId, current - 1, maxAllowed);
            this.lastInteractedVariantId = variantId;
            this.scheduleVariantSync(variant);
            this.renderSizes();
            this.updateSubmitState();
            this.scheduleScrollRestore();
          });

          const qtyInput = document.createElement('input');
          qtyInput.type = 'number';
          qtyInput.inputMode = 'numeric';
          qtyInput.pattern = '[0-9]*';
          qtyInput.min = '0';
          qtyInput.max = String(maxAllowed);
          qtyInput.step = '1';
          qtyInput.className = 'pibulk__qty-input pibulk__qty-value';
          qtyInput.value = String(quantity);
          qtyInput.disabled = !available || blockedByCartAllocation;
          qtyInput.setAttribute('aria-label', `Quantity for ${sizeLabel}`);

          qtyInput.addEventListener('input', () => {
            this.setVariantQuantity(variantId, qtyInput.value, maxAllowed);
            this.updateSubmitState();
          });

          const commitTypedQuantity = () => {
            this.captureScrollPosition();
            this.setVariantQuantity(variantId, qtyInput.value, maxAllowed);
            this.lastInteractedVariantId = variantId;
            this.scheduleVariantSync(variant);
            this.renderSizes();
            this.updateSubmitState();
            this.scheduleScrollRestore();
          };
          qtyInput.addEventListener('change', commitTypedQuantity);

          const increaseButton = document.createElement('button');
          increaseButton.type = 'button';
          increaseButton.className = 'pibulk__qty-btn';
          increaseButton.textContent = '+';
          increaseButton.disabled = !canIncrease;
          increaseButton.addEventListener('click', () => {
            this.captureScrollPosition();
            const current = Number(this.quantities[variantId] || 0);
            this.setVariantQuantity(variantId, current + 1, maxAllowed);
            this.lastInteractedVariantId = variantId;
            this.scheduleVariantSync(variant);
            this.renderSizes();
            this.updateSubmitState();
            this.scheduleScrollRestore();
          });

          qtyWrap.append(decreaseButton, qtyInput, increaseButton);

          if (isOutOfStock) {
            const outOfStockText = document.createElement('p');
            outOfStockText.className = 'pibulk__available pibulk__available--out-of-stock';
            outOfStockText.textContent = 'Out of stock';

            const notifyButton = document.createElement('button');
            notifyButton.type = 'button';
            notifyButton.className = 'pibulk__notify-btn';
            notifyButton.innerHTML = '<span>Notify me when</span><span>in stock</span>';
            notifyButton.addEventListener('click', () => {
              this.openBackInStockModal(variant);
            });
            card.append(sizeName, outOfStockText, notifyButton);
          } else {
            const priceText = document.createElement('p');
            priceText.className = 'pibulk__price';
            priceText.textContent = this.formatMoney(this.getDisplayPriceCents(variant.price, volumeDiscountState));

            const availableText = document.createElement('p');
            availableText.className = 'pibulk__available';
            availableText.textContent = this.getAvailabilityText(variant, cartQuantity);

            card.append(sizeName, qtyWrap, priceText, availableText);

            if (blockedByCartAllocation && cartQuantity > 0) {
              const inCartText = document.createElement('p');
              inCartText.className = 'pibulk__in-cart';
              inCartText.textContent = `${cartQuantity} in cart`;
              card.appendChild(inCartText);
            }
          }
          this.sizesElement.appendChild(card);
        });

        if (!visibleVariants.length) {
          const emptyState = document.createElement('p');
          emptyState.className = 'pibulk__sizes-empty';
          emptyState.textContent = this.showOutOfStockVariants
            ? 'No options match the current selection.'
            : 'No in-stock options match the current selection.';
          this.sizesElement.appendChild(emptyState);
        }

        this.renderVolumeDiscountTiers(variants, volumeDiscountState);
        this.updateMainProductDisplayPrice(variants, volumeDiscountState);
        this.renderGwpTiers();
      }

      retainVisibleQuantities() {
        Object.keys(this.quantities).forEach((variantId) => {
          if (Number(this.quantities[variantId]) <= 0) {
            delete this.quantities[variantId];
          }
        });
      }

      getTotalQuantity() {
        let total = 0;
        Object.keys(this.quantities).forEach((variantId) => {
          total += Number(this.quantities[variantId] || 0);
        });
        return total;
      }

      getSubmitLabel(total) {
        const safeTotal = Math.max(0, Number(total) || 0);
        return safeTotal < 1
          ? 'Make a selection'
          : `Add ${safeTotal} item${safeTotal === 1 ? '' : 's'} to basket`;
      }

      setSubmitButtonLabel(label) {
        if (!this.submitButton) return;
        const nextLabel = String(label || '');
        const textElement = this.submitButton.querySelector('.btn__text');
        if (textElement) {
          textElement.textContent = nextLabel;
          return;
        }
        this.submitButton.textContent = nextLabel;
      }

      startSubmitDotsAnimation(baseLabel) {
        if (!this.submitButton) return;
        this.setSubmitButtonLabel(baseLabel);
        this.submitButton.classList.add('btn--loading');
      }

      stopSubmitDotsAnimation() {
        if (this.submitDotsTimer) {
          window.clearInterval(this.submitDotsTimer);
          this.submitDotsTimer = null;
        }
        this.submitDotsFrame = 0;
        this.submitDotsBaseLabel = '';
      }

      updateSubmitState() {
        if (!this.submitButton) return;
        const total = this.getTotalQuantity();
        const displayTotal = this.isSubmitting ? Math.max(1, Number(this.submittingTotal) || 0) : total;
        const baseLabel = this.getSubmitLabel(displayTotal);
        this.submitButton.disabled = total < 1 || this.isSubmitting;
        if (this.isSubmitting) {
          this.setSubmitButtonLabel(baseLabel);
          this.submitButton.classList.add('btn--loading');
          return;
        }

        this.stopSubmitDotsAnimation();
        this.submitButton.classList.remove('btn--loading');
        this.setSubmitButtonLabel(baseLabel);
      }

      async handleSubmit() {
        if (this.isSubmitting) return;

        const items = [];
        Object.keys(this.quantities).forEach((variantId) => {
          const quantity = Number(this.quantities[variantId] || 0);
          if (quantity < 1) return;
          const variant = this.getVariantById(variantId);
          if (!variant) return;
          items.push({ id: variant.id, quantity });
        });

        if (!items.length) return;

        this.submittingTotal = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        this.isSubmitting = true;
        this.updateSubmitState();
        this.setStatus('', '');

        try {
          let beforeCart = null;
          try {
            beforeCart = await this.fetchCartJson();
          } catch (error) {
            beforeCart = null;
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) throw new Error('Unable to add items to basket.');
          await response.json();

          const cart = await this.notifyCartUpdated();
          const addedEntries = this.collectAddedEntries(beforeCart, cart, items);

          this.quantities = {};
          this.renderSizes();
          this.setStatus('', '');
          this.showAddedModal(addedEntries, cart);
        } catch (error) {
          this.setStatus(error.message || 'Unable to add items to basket.', 'is-error');
        } finally {
          this.isSubmitting = false;
          this.submittingTotal = 0;
          this.updateSubmitState();
        }
      }

      async notifyCartUpdated() {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: false },
          })
        );

        try {
          const cart = await this.fetchCartJson();

          if (
            window.FoxTheme &&
            window.FoxTheme.pubsub &&
            window.FoxTheme.pubsub.publish &&
            window.FoxTheme.pubsub.PUB_SUB_EVENTS &&
            window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
          ) {
            window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-info-bulk-order',
              cart,
            });
          }

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              detail: { cart },
            })
          );

          return cart;
        } catch (error) {
          // best-effort cart sync only
        }

        return null;
      }

      setStatus(message, stateClass) {
        if (!this.statusElement) return;
        this.statusElement.classList.remove('is-error', 'is-success');
        if (stateClass) this.statusElement.classList.add(stateClass);
        this.statusElement.textContent = message;
      }
    }
  );
}
